import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import CriteriaChecklist, { CriterionDef, CriterionValue } from '../components/ui/CriteriaChecklist';
import AllocationPreviewPanel from '../components/ui/AllocationPreviewPanel';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
// Phase 16a1 / 16b1 reviewer panels — open from the review detail modal's POB rows.
import PrincipalAgentAffirmationPanel from './PrincipalAgentAffirmationPanel';
import WarrantyAssessmentPanel from './WarrantyAssessmentPanel';

const API = `${API_V1}/revenue/reviews`;
const CONTRACTS_API = `${API_V1}/revenue/contracts`;

// ── ASC 606 Step 1 criteria (606-10-25-1) ────────────────────

const ASC606_CRITERIA: CriterionDef[] = [
  {
    key: '1',
    label: 'Parties have approved the contract',
    description: 'Both parties have approved the contract (in writing, orally, or in accordance with customary business practice) and are committed to perform.',
    notesPlaceholder: 'e.g. signed PDF dated 2026-04-10, countersigned by both parties',
  },
  {
    key: '2',
    label: 'Each party\'s rights are identifiable',
    description: 'The contract identifies each party\'s rights regarding the goods or services to be transferred.',
    notesPlaceholder: 'e.g. Section 2 enumerates deliverables and acceptance criteria',
  },
  {
    key: '3',
    label: 'Payment terms are identifiable',
    description: 'The contract identifies the payment terms for the goods or services.',
    notesPlaceholder: 'e.g. Net-30 on invoice; milestone schedule in Exhibit A',
  },
  {
    key: '4',
    label: 'Contract has commercial substance',
    description: 'The contract has commercial substance — the risk, timing, or amount of the entity\'s future cash flows is expected to change as a result.',
    notesPlaceholder: 'e.g. arms-length pricing; customer relationship not circular',
  },
  {
    key: '5',
    label: 'Collectibility is probable',
    description: 'It is probable that the entity will collect the consideration to which it will be entitled in exchange for the goods or services.',
    notesPlaceholder: 'e.g. customer credit check PASS; no prior aging issues',
  },
];

// ── Types ─────────────────────────────────────────────────

interface Review {
  id: number;
  revenue_contract_id: number;
  contract_number: string;
  customer_name: string | null;
  entity_code: string | null;
  total_value: number;
  currency: string | null;
  start_date: string | null;
  end_date: string | null;
  contract_status: string;
  review_status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewer_id: number | null;
  reviewed_at: string | null;
  criterion_1_approval: boolean | null; criterion_1_notes: string | null;
  criterion_2_rights: boolean | null;   criterion_2_notes: string | null;
  criterion_3_payment: boolean | null;  criterion_3_notes: string | null;
  criterion_4_commercial: boolean | null; criterion_4_notes: string | null;
  criterion_5_collectibility: boolean | null; criterion_5_notes: string | null;
  collectibility_status: string | null;
  collectibility_notes: string | null;
  rejection_reason: string | null;
  source: 'REVIEWER' | 'BACKFILL';
  // ASC 606 Phase 7c — enforceable term
  term_type: 'FIXED' | 'EVERGREEN';
  enforceable_term_months: number | null;
  review_enforceable_term_months: number | null;
  // ASC 606 Phase 8b — Step 2 distinctness affirmation
  step2_distinctness_affirmed: boolean | null;
  step2_distinctness_notes: string | null;
  // ASC 606 Phase 8c — Step 2 warranty affirmation
  step2_warranty_affirmed: boolean | null;
  step2_warranty_notes: string | null;
  // ASC 606 Phase 8d — Step 2 material right affirmation
  step2_material_right_affirmed: boolean | null;
  step2_material_right_notes: string | null;
  // ASC 606 Phase 8e — Step 2 principal-vs-agent affirmation
  step2_principal_agent_affirmed: boolean | null;
  step2_principal_agent_notes: string | null;
  // ASC 606 Phase 9a — Step 3 variable consideration affirmation
  step3_variable_consideration_affirmed: boolean | null;
  step3_variable_consideration_notes: string | null;
  // ASC 606 Phase 9c — Step 3 financing + noncash affirmation
  step3_financing_affirmed: boolean | null;
  step3_financing_notes: string | null;
  step3_noncash_affirmed: boolean | null;
  step3_noncash_notes: string | null;
  financing_effective_rate?: number | null;
  financing_adjustment?: number | null;
  noncash_consideration_fv?: number | null;
  noncash_description?: string | null;
  // ASC 606 Phase 9e — Step 3 CPS affirmation
  step3_cps_affirmed: boolean | null;
  step3_cps_notes: string | null;
  // ASC 606 Phase 10e — Step 4 allocation attestations
  step4_allocation_affirmed: boolean | null;
  step4_allocation_notes: string | null;
  step4_specific_discount_affirmed: boolean | null;
  step4_specific_discount_notes: string | null;
  step4_sd_criterion_standalone: boolean | null;
  step4_sd_criterion_bundle_discount: boolean | null;
  step4_sd_criterion_observable_evidence: boolean | null;
  step4_vc_specific_affirmed: boolean | null;
  step4_vc_specific_notes: string | null;
  step4_vc_criterion_terms_specific: boolean | null;
  step4_vc_criterion_faithful_depiction: boolean | null;
  step4_residual_attested: boolean | null;
  step4_residual_notes: string | null;
  step4_residual_condition: string | null;
  // ASC 340-40 Phase 12d — Step 6 contract cost attestations
  step6_contract_costs_reviewed_affirmed: boolean | null;
  step6_contract_costs_reviewed_notes: string | null;
  step6_recoverable_affirmed: boolean | null;
  step6_recoverable_notes: string | null;
  step6_amortization_method_affirmed: boolean | null;
  step6_amortization_method_notes: string | null;
  step6_impairment_tested_affirmed: boolean | null;
  step6_impairment_tested_notes: string | null;
  step6_25_4_expedient_election_affirmed: boolean | null;
  step6_25_4_expedient_election_notes: string | null;
  step6_approved_at: string | null;
  // ASC 606 Phase 11f — Step 5 recognition attestations
  step5_over_time_qualified_affirmed: boolean | null;
  step5_over_time_qualified_notes: string | null;
  step5_over_time_criterion_a_simultaneous_consumption: boolean | null;
  step5_over_time_criterion_b_customer_controls: boolean | null;
  step5_over_time_criterion_c_no_alternative_use_and_right_to_payment: boolean | null;
  step5_progress_method_affirmed: boolean | null;
  step5_progress_method_notes: string | null;
  step5_cost_baseline_affirmed: boolean | null;
  step5_cost_baseline_notes: string | null;
  step5_uninstalled_materials_carved_out_affirmed: boolean | null;
  step5_uninstalled_materials_carved_out_notes: string | null;
  step5_milestones_reasonable_measure_affirmed: boolean | null;
  step5_milestones_reasonable_measure_notes: string | null;
  step5_units_carve_out_affirmed: boolean | null;
  step5_units_carve_out_notes: string | null;
  step5_r2i_expedient_affirmed: boolean | null;
  step5_r2i_expedient_notes: string | null;
  step5_onerous_contract_reviewed: boolean | null;
  step5_onerous_contract_notes: string | null;
  step5_approved_at: string | null;
  created_at: string | null;
  reviewed_at_display?: string;
}

// ASC 606 Phase 9c — financing assessment result
interface FinancingResult {
  contract_id: number;
  contract_number: string;
  face_value: number;
  duration_months: number | null;
  applies_practical_expedient: boolean;
  rate_source: 'CONTRACT' | 'TENANT' | 'STORED' | 'NONE' | 'N/A';
  effective_rate: number | null;
  present_value: number;
  adjustment: number;
  significance_threshold: number | null;
  significant: boolean;
}

// ASC 606 Phase 9c — noncash assessment result
interface NoncashResult {
  contract_id: number;
  contract_number: string;
  detected: boolean;
  matched_keyword: string | null;
  rationale: string;
  noncash_consideration_fv: number | null;
  noncash_description: string | null;
  fv_recorded: boolean;
}

// ASC 606 Phase 9a — variable consideration classifier result
interface VariableComponent {
  id: number;
  component_type: 'REBATE' | 'BONUS' | 'PENALTY' | 'REFUND' | 'DISCOUNT' | 'ESCALATION';
  estimation_method: 'EXPECTED_VALUE' | 'MOST_LIKELY_AMOUNT';
  estimated_amount: number;
  probability: number | null;
  constraint_applied: boolean;
  constrained_amount: number;
  pending_adjustment_amount: number;
  status: string;
  rationale: string | null;
  scenarios: unknown;
  assessed_at: string | null;
  last_reassessed_at: string | null;
}

interface VariableConsiderationResult {
  contract_id: number;
  contract_number: string;
  total_value: number;
  variable_consideration_aggregate: number;
  constraint_probability_threshold: number | null;
  assessed_at: string | null;
  components: VariableComponent[];
  has_unquantified_components: boolean;
  has_components: boolean;
}

// ASC 606 Phase 9e — consideration payable to customer row
interface CPSRow {
  id: number;
  payment_type: string;
  classification: 'DISTINCT_SERVICE_RECEIVED' | 'TRANSACTION_PRICE_REDUCTION';
  description: string;
  gross_amount: number;
  distinct_service_fair_value: number | null;
  expense_amount: number;
  tp_reduction_amount: number;
  status: 'OPEN' | 'ACCRUED' | 'SETTLED' | 'RELEASED';
  accrued_amount: number;
  settled_amount: number;
  balance_due: number;
}

// ASC 606 Phase 9d — escalation history entry (from VariableComponentHistory)
interface EscalationEvent {
  id: number;
  changed_at: string | null;
  delta_amount: number | null;
  constrained_amount: number;
  rationale: string | null;
  changed_by: number | null;
}

// ASC 606 Phase 9b — refund liability summary shown inside Step 3 section
interface RefundLiability {
  id: number;
  variable_component_id: number | null;
  initial_amount: number;
  accrued_amount: number;
  settled_amount: number;
  released_amount: number;
  liability_balance: number;
  status: 'OPEN' | 'ACCRUED' | 'SETTLED' | 'RELEASED';
  accrual_je_id: number | null;
  settlement_je_ids: number[];
  release_je_id: number | null;
  notes: string | null;
}

// ASC 606 Phase 8c — warranty classifier result
interface WarrantyPob {
  pob_id: number;
  contract_id: number;
  po_number: number;
  obligation_name: string | null;
  description: string | null;
  warranty_type: 'NONE' | 'ASSURANCE' | 'SERVICE';
  warranty_term_months: number | null;
  warranty_accrual_rate: number | null;
  rationale: string;
  classified_at?: string | null;
}

interface WarrantyResult {
  contract_id: number;
  primary_contract_id: number;
  pool_size: number;
  assessed_at: string | null;
  buckets: { NONE: number; ASSURANCE: number; SERVICE: number };
  pobs: WarrantyPob[];
  has_service_warranty: boolean;
}

// ASC 606 Phase 8d — material right classifier result
interface MaterialRightPob {
  pob_id: number;
  contract_id: number;
  po_number: number;
  obligation_name: string | null;
  description: string | null;
  is_material_right: boolean;
  material_right_type: 'RENEWAL_OPTION' | 'FUTURE_PURCHASE_DISCOUNT' | 'LOYALTY' | 'OTHER' | null;
  expected_consideration: number | null;
  exercise_probability: number | null;
  rationale: string;
  classified_at?: string | null;
}

interface MaterialRightResult {
  contract_id: number;
  primary_contract_id: number;
  pool_size: number;
  assessed_at: string | null;
  type_counts: {
    RENEWAL_OPTION: number;
    FUTURE_PURCHASE_DISCOUNT: number;
    LOYALTY: number;
    OTHER: number;
  };
  pobs: MaterialRightPob[];
  has_material_right: boolean;
  requires_ssp_library: boolean;
}

// ASC 606 Phase 8g — contract components (lease bifurcation)
interface ContractComponent {
  id: number;
  component_type: 'LEASE' | 'REVENUE';
  allocated_amount: number;
  basis_note: string | null;
  lease_id: number | null;
  created_at: string | null;
}

interface ContractComponentsResult {
  revenue_contract_id: number;
  contract_number: string;
  current_total_value: number;
  components: ContractComponent[];
}

// ASC 606 Phase 8e — principal-vs-agent classifier result
interface PaIndicator { value: boolean; evidence: string; }
interface PaPob {
  pob_id: number;
  contract_id: number;
  po_number: number;
  obligation_name: string | null;
  description: string | null;
  entity_role: 'PRINCIPAL' | 'AGENT' | 'UNDETERMINED';
  indicators: {
    primary_responsibility: PaIndicator;
    inventory_risk: PaIndicator;
    price_discretion: PaIndicator;
  } | null;
  rationale: string;
  classified_at?: string | null;
}

interface PrincipalAgentResult {
  contract_id: number;
  primary_contract_id: number;
  pool_size: number;
  assessed_at: string | null;
  counts: { PRINCIPAL: number; AGENT: number; UNDETERMINED: number };
  predominant_role: 'ALL_PRINCIPAL' | 'ALL_AGENT' | 'MIXED' | 'UNDETERMINED';
  pobs: PaPob[];
  has_agent: boolean;
  has_undetermined: boolean;
}

// ASC 606 Phase 8a — distinctness classifier result
interface DistinctnessPob {
  pob_id: number;
  contract_id: number;
  po_number: number;
  obligation_name: string | null;
  description: string | null;
  capable: boolean;
  separable: boolean;
  combine_reason: 'INTEGRATION' | 'MODIFICATION' | 'INTERDEPENDENT' | null;
  matched_phrase?: string | null;
  rationale: string;
  assessed_at?: string | null;
  bundled_with_id?: number | null;
}

interface DistinctnessResult {
  contract_id: number;
  primary_contract_id: number;
  pool_size: number;
  assessed_at: string | null;
  pobs: DistinctnessPob[];
  suggested_merge_groups: Array<{
    combine_reason: 'INTEGRATION' | 'MODIFICATION' | 'INTERDEPENDENT';
    members: Array<{ pob_id: number; contract_id: number; po_number: number; obligation_name: string | null }>;
  }>;
}

const COMBINE_REASON_CITE: Record<string, string> = {
  INTEGRATION: '606-10-25-21(a) — significant integration service',
  MODIFICATION: '606-10-25-21(b) — significant modification/customization',
  INTERDEPENDENT: '606-10-25-21(c) — highly interdependent/interrelated',
};

// ── Columns ───────────────────────────────────────────────

const statusBadge = (status: string) => {
  const map: Record<string, { bg: string; color: string }> = {
    PENDING: { bg: 'rgba(234,179,8,0.15)', color: 'var(--warning)' },
    APPROVED: { bg: 'rgba(34,197,94,0.15)', color: 'var(--positive)' },
    REJECTED: { bg: 'rgba(239,68,68,0.15)', color: 'var(--negative)' },
  };
  const s = map[status] || { bg: 'var(--bg-hover)', color: 'var(--text-secondary)' };
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: s.bg, color: s.color, fontSize: 11, fontWeight: 600 }}>
      {status}
    </span>
  );
};

const sourceBadge = (source: string) => (
  <span style={{
    display: 'inline-block', padding: '1px 6px', borderRadius: 3,
    fontSize: 10, fontWeight: 600,
    background: source === 'BACKFILL' ? 'var(--bg-hover)' : 'transparent',
    color: source === 'BACKFILL' ? 'var(--text-secondary)' : 'var(--accent)',
    border: source === 'BACKFILL' ? '1px solid var(--border-subtle)' : '1px solid var(--accent)',
  }}>
    {source}
  </span>
);

export default function RevenueReviews() {
  const { entity } = useGlobalEntity();
  const [tab, setTab] = useState<'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL'>('PENDING');
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [allCounts, setAllCounts] = useState<{ pending: number; approved: number; rejected: number }>({ pending: 0, approved: 0, rejected: 0 });
  const [selected, setSelected] = useState<Review | null>(null);
  const [mode, setMode] = useState<'VIEW' | 'APPROVE' | 'REJECT'>('VIEW');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Form state
  const [criteriaValues, setCriteriaValues] = useState<Record<string, CriterionValue>>({});
  const [collectibilityStatus, setCollectibilityStatus] = useState<'PROBABLE' | 'NOT_PROBABLE'>('PROBABLE');
  const [collectibilityNotes, setCollectibilityNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [enforceableTerm, setEnforceableTerm] = useState<string>('');

  // Phase 7f — combination candidates
  const [candidates, setCandidates] = useState<any[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [combineModalOpen, setCombineModalOpen] = useState(false);
  const [combineTarget, setCombineTarget] = useState<any>(null);  // the candidate being combined into the selected review's contract
  const [combineReason, setCombineReason] = useState('');
  const [combineSubmitting, setCombineSubmitting] = useState(false);

  // ASC 606 Phase 8b — distinctness state
  const [distinctness, setDistinctness] = useState<DistinctnessResult | null>(null);
  const [distinctnessLoading, setDistinctnessLoading] = useState(false);
  const [step2Affirmed, setStep2Affirmed] = useState(false);
  const [step2Notes, setStep2Notes] = useState('');

  // ASC 606 Phase 8c — warranty state
  const [warranty, setWarranty] = useState<WarrantyResult | null>(null);
  const [warrantyLoading, setWarrantyLoading] = useState(false);
  const [step2WarrantyAffirmed, setStep2WarrantyAffirmed] = useState(false);
  const [step2WarrantyNotes, setStep2WarrantyNotes] = useState('');

  // ASC 606 Phase 8d — material right state
  const [materialRight, setMaterialRight] = useState<MaterialRightResult | null>(null);
  const [materialRightLoading, setMaterialRightLoading] = useState(false);
  const [step2MaterialRightAffirmed, setStep2MaterialRightAffirmed] = useState(false);
  const [step2MaterialRightNotes, setStep2MaterialRightNotes] = useState('');

  // ASC 606 Phase 8e — principal-vs-agent state
  const [principalAgent, setPrincipalAgent] = useState<PrincipalAgentResult | null>(null);
  const [principalAgentLoading, setPrincipalAgentLoading] = useState(false);
  const [step2PaAffirmed, setStep2PaAffirmed] = useState(false);
  const [step2PaNotes, setStep2PaNotes] = useState('');

  // Phase 16a1 / 16b1 — per-POB reviewer panel open state.
  // Each holds the pob_id (+ optional label) that currently has its panel open;
  // null means closed.
  const [paPanelPob, setPaPanelPob] = useState<{ id: number; label?: string } | null>(null);
  const [warrantyPanelPob, setWarrantyPanelPob] = useState<{ id: number; label?: string; recognitionMethod?: string | null } | null>(null);

  // ASC 606 Phase 9a — variable consideration state
  const [variableConsideration, setVariableConsideration] = useState<VariableConsiderationResult | null>(null);
  const [variableConsiderationLoading, setVariableConsiderationLoading] = useState(false);
  const [variableConsiderationError, setVariableConsiderationError] = useState<string | null>(null);
  const [step3VcAffirmed, setStep3VcAffirmed] = useState(false);
  const [step3VcNotes, setStep3VcNotes] = useState('');
  // ASC 606 Phase 9b — refund liabilities for the selected contract
  const [refundLiabilities, setRefundLiabilities] = useState<RefundLiability[]>([]);
  // ASC 606 Phase 9c — financing + noncash state
  const [financing, setFinancing] = useState<FinancingResult | null>(null);
  const [financingLoading, setFinancingLoading] = useState(false);
  const [financingError, setFinancingError] = useState<string | null>(null);
  const [step3FinancingAffirmed, setStep3FinancingAffirmed] = useState(false);
  const [step3FinancingNotes, setStep3FinancingNotes] = useState('');
  const [noncash, setNoncash] = useState<NoncashResult | null>(null);
  const [noncashLoading, setNoncashLoading] = useState(false);
  const [step3NoncashAffirmed, setStep3NoncashAffirmed] = useState(false);
  const [step3NoncashNotes, setStep3NoncashNotes] = useState('');
  const [noncashFvInput, setNoncashFvInput] = useState('');
  const [noncashDescInput, setNoncashDescInput] = useState('');
  // ASC 606 Phase 9d — escalation events + apply form
  const [escalationHistory, setEscalationHistory] = useState<EscalationEvent[]>([]);
  const [escalationDelta, setEscalationDelta] = useState('');
  const [escalationDate, setEscalationDate] = useState('');
  const [escalationDesc, setEscalationDesc] = useState('');
  const [escalationSubmitting, setEscalationSubmitting] = useState(false);
  const [escalationError, setEscalationError] = useState<string | null>(null);
  // ASC 606 Phase 9e — CPS state + create form
  const [cpsRows, setCpsRows] = useState<CPSRow[]>([]);
  const [step3CpsAffirmed, setStep3CpsAffirmed] = useState(false);
  const [step3CpsNotes, setStep3CpsNotes] = useState('');
  // ASC 606 Phase 10e — Step 4 allocation attestation state
  const [step4AllocAffirmed, setStep4AllocAffirmed] = useState(false);
  const [step4AllocNotes, setStep4AllocNotes] = useState('');
  const [step4SdAffirmed, setStep4SdAffirmed] = useState(false);
  const [step4SdNotes, setStep4SdNotes] = useState('');
  const [step4SdCrit1, setStep4SdCrit1] = useState(false);
  const [step4SdCrit2, setStep4SdCrit2] = useState(false);
  const [step4SdCrit3, setStep4SdCrit3] = useState(false);
  const [step4VcSpecAffirmed, setStep4VcSpecAffirmed] = useState(false);
  const [step4VcSpecNotes, setStep4VcSpecNotes] = useState('');
  const [step4VcCrit1, setStep4VcCrit1] = useState(false);
  const [step4VcCrit2, setStep4VcCrit2] = useState(false);
  const [step4ResAttested, setStep4ResAttested] = useState(false);
  const [step4ResNotes, setStep4ResNotes] = useState('');
  const [step4ResCondition, setStep4ResCondition] = useState<string>('');

  // ASC 606 Phase 11f — Step 5 recognition attestation state
  const [step5OtAffirmed, setStep5OtAffirmed] = useState(false);
  const [step5OtNotes, setStep5OtNotes] = useState('');
  const [step5OtCritA, setStep5OtCritA] = useState<boolean | null>(null);
  const [step5OtCritB, setStep5OtCritB] = useState<boolean | null>(null);
  const [step5OtCritC, setStep5OtCritC] = useState<boolean | null>(null);
  const [step5PmAffirmed, setStep5PmAffirmed] = useState(false);
  const [step5PmNotes, setStep5PmNotes] = useState('');
  const [step5CostBaselineAffirmed, setStep5CostBaselineAffirmed] = useState(false);
  const [step5CostBaselineNotes, setStep5CostBaselineNotes] = useState('');
  const [step5UninstalledAffirmed, setStep5UninstalledAffirmed] = useState(false);
  const [step5UninstalledNotes, setStep5UninstalledNotes] = useState('');
  const [step5MilestonesAffirmed, setStep5MilestonesAffirmed] = useState(false);
  const [step5MilestonesNotes, setStep5MilestonesNotes] = useState('');
  const [step5UnitsAffirmed, setStep5UnitsAffirmed] = useState(false);
  const [step5UnitsNotes, setStep5UnitsNotes] = useState('');
  const [step5R2iAffirmed, setStep5R2iAffirmed] = useState(false);
  const [step5R2iNotes, setStep5R2iNotes] = useState('');
  const [step5OnerousAffirmed, setStep5OnerousAffirmed] = useState(false);
  const [step5OnerousNotes, setStep5OnerousNotes] = useState('');
  // Phase 11g — pre-flight blocking detection (hydrated from satisfaction + POB scan)
  const [step5PreflightBlocks, setStep5PreflightBlocks] = useState<string[]>([]);

  // ASC 340-40 Phase 12d — Step 6 contract cost attestation state
  const [step6CcReviewed, setStep6CcReviewed] = useState(false);
  const [step6CcReviewedNotes, setStep6CcReviewedNotes] = useState('');
  const [step6Recoverable, setStep6Recoverable] = useState(false);
  const [step6RecoverableNotes, setStep6RecoverableNotes] = useState('');
  const [step6AmortMethod, setStep6AmortMethod] = useState(false);
  const [step6AmortMethodNotes, setStep6AmortMethodNotes] = useState('');
  const [step6ImpTested, setStep6ImpTested] = useState(false);
  const [step6ImpTestedNotes, setStep6ImpTestedNotes] = useState('');
  const [step6Expedient, setStep6Expedient] = useState(false);
  const [step6ExpedientNotes, setStep6ExpedientNotes] = useState('');
  // Step 6 pre-flight detection
  const [step6PreflightBlocks, setStep6PreflightBlocks] = useState<string[]>([]);
  const [step6HasAnyCostAsset, setStep6HasAnyCostAsset] = useState(false);
  const [step6HasCapitalizedAsset, setStep6HasCapitalizedAsset] = useState(false);
  const [step6HasExpedientAsset, setStep6HasExpedientAsset] = useState(false);
  const [step6HasImpairmentHistory, setStep6HasImpairmentHistory] = useState(false);
  const [step5HasOverTimePob, setStep5HasOverTimePob] = useState(false);
  const [step5HasCostToCost, setStep5HasCostToCost] = useState(false);
  const [step5HasMilestones, setStep5HasMilestones] = useState(false);
  const [step5HasUnits, setStep5HasUnits] = useState(false);
  const [step5HasR2i, setStep5HasR2i] = useState(false);
  const [step5HasPendingRevision, setStep5HasPendingRevision] = useState(false);
  const [step5HasOpenOnerous, setStep5HasOpenOnerous] = useState(false);
  const [cpsForm, setCpsForm] = useState({
    payment_type: 'REBATE',
    classification: 'TRANSACTION_PRICE_REDUCTION',
    description: '',
    gross_amount: '',
    distinct_service_fair_value: '',
  });
  const [cpsSubmitting, setCpsSubmitting] = useState(false);
  const [cpsError, setCpsError] = useState<string | null>(null);

  // ASC 606 Phase 8f — merge modal state
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeGroup, setMergeGroup] = useState<null | { combine_reason: string; members: Array<{ pob_id: number; po_number: number; obligation_name: string | null }> }>(null);
  const [mergeJustification, setMergeJustification] = useState('');
  const [mergeForcedMethod, setMergeForcedMethod] = useState('');
  const [mergeSubmitting, setMergeSubmitting] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);

  // ASC 606 Phase 8g — lease bifurcation state
  const [components, setComponents] = useState<ContractComponentsResult | null>(null);
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [bifurcateModalOpen, setBifurcateModalOpen] = useState(false);
  const [bifurcateForm, setBifurcateForm] = useState({
    allocated_amount: '',
    description: '',
    asset_type: '',
    lessor_name: '',
    commencement_date: '',
    end_date: '',
    monthly_payment: '',
    discount_rate: '0.05',
    framework: 'IFRS16',
    classification: 'OPERATING',
    basis_note: '',
  });
  const [bifurcateSubmitting, setBifurcateSubmitting] = useState(false);
  const [bifurcateError, setBifurcateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch current tab + counts in parallel
      const tabQuery = tab === 'ALL' ? '' : `?status=${tab}`;
      const qs = tabQuery ? (tabQuery.includes('?') ? `${tabQuery}&` : '?') : '?';
      const tabUrl = withEntityParam(`${API}${tabQuery}`, entity);

      // For counts, fetch each status — bounded by limit=1000 default on backend, fine for this volume
      const [tabRes, pendRes, appRes, rejRes] = await Promise.all([
        authFetch(tabUrl),
        authFetch(withEntityParam(`${API}?status=PENDING`, entity)),
        authFetch(withEntityParam(`${API}?status=APPROVED`, entity)),
        authFetch(withEntityParam(`${API}?status=REJECTED`, entity)),
      ]);
      if (tabRes.ok) setReviews(((await tabRes.json()).reviews || []) as Review[]);
      const pend = pendRes.ok ? ((await pendRes.json()).reviews || []).length : 0;
      const app = appRes.ok ? ((await appRes.json()).reviews || []).length : 0;
      const rej = rejRes.ok ? ((await rejRes.json()).reviews || []).length : 0;
      setAllCounts({ pending: pend, approved: app, rejected: rej });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity, tab]);

  useEffect(() => { load(); }, [load]);

  const openReview = (r: Review, m: 'VIEW' | 'APPROVE' | 'REJECT') => {
    setSelected(r);
    setMode(m);
    setFormError(null);
    // Pre-fill from existing review data if APPROVED/REJECTED, else fresh
    const initial: Record<string, CriterionValue> = {
      '1': { value: r.criterion_1_approval, notes: r.criterion_1_notes || '' },
      '2': { value: r.criterion_2_rights, notes: r.criterion_2_notes || '' },
      '3': { value: r.criterion_3_payment, notes: r.criterion_3_notes || '' },
      '4': { value: r.criterion_4_commercial, notes: r.criterion_4_notes || '' },
      '5': { value: r.criterion_5_collectibility, notes: r.criterion_5_notes || '' },
    };
    // For APPROVE mode on a PENDING review, default all criteria to false
    // (reviewer must explicitly affirm each one). Notes stay empty.
    if (m === 'APPROVE' && r.review_status === 'PENDING') {
      for (const k of ['1', '2', '3', '4', '5']) initial[k] = { value: false, notes: '' };
    }
    setCriteriaValues(initial);
    setCollectibilityStatus((r.collectibility_status as 'PROBABLE' | 'NOT_PROBABLE') || 'PROBABLE');
    setCollectibilityNotes(r.collectibility_notes || '');
    setRejectionReason(r.rejection_reason || '');
    setEnforceableTerm(
      r.enforceable_term_months != null
        ? String(r.enforceable_term_months)
        : r.review_enforceable_term_months != null
          ? String(r.review_enforceable_term_months)
          : '',
    );
    // Phase 7f — fetch combination candidates for PENDING reviews only
    setCandidates([]);
    if (r.review_status === 'PENDING' && m !== 'VIEW') {
      setCandidatesLoading(true);
      authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/combination-candidates`)
        .then(res => res.ok ? res.json() : null)
        .then(d => setCandidates(d?.candidates || []))
        .catch(() => setCandidates([]))
        .finally(() => setCandidatesLoading(false));
    }

    // Phase 8b — fetch distinctness; auto-run assess if pool has >1 POB and
    // nothing classified yet. For VIEW mode (approved/rejected historic), just
    // read what's there. Default affirmation: TRUE when clean, FALSE when dirty.
    setDistinctness(null);
    setStep2Affirmed(!!r.step2_distinctness_affirmed);
    setStep2Notes(r.step2_distinctness_notes || '');
    // Phase 8c — warranty state reset
    setWarranty(null);
    setStep2WarrantyAffirmed(!!r.step2_warranty_affirmed);
    setStep2WarrantyNotes(r.step2_warranty_notes || '');
    // Phase 8g — components fetch (read-only display on modal open)
    setComponents(null);
    setComponentsLoading(true);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/components`)
      .then(res => res.ok ? res.json() : null)
      .then((d: ContractComponentsResult | null) => setComponents(d))
      .catch(() => setComponents(null))
      .finally(() => setComponentsLoading(false));
    // Phase 8e — principal-vs-agent state reset + fetch
    setPrincipalAgent(null);
    setStep2PaAffirmed(!!r.step2_principal_agent_affirmed);
    setStep2PaNotes(r.step2_principal_agent_notes || '');
    setPrincipalAgentLoading(true);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/principal-agent-result`)
      .then(res => res.ok ? res.json() : null)
      .then(async (d: PrincipalAgentResult | null) => {
        if (!d) return null;
        const needsAssess =
          m !== 'VIEW' &&
          r.review_status === 'PENDING' &&
          d.pool_size > 1 &&
          !d.pobs.some(p => p.classified_at);
        if (!needsAssess) return d;
        const runRes = await authFetch(
          `${CONTRACTS_API}/${r.revenue_contract_id}/assess-principal-agent`,
          { method: 'POST' },
        );
        if (runRes.ok) return (await runRes.json()) as PrincipalAgentResult;
        return d;
      })
      .then((d: PrincipalAgentResult | null) => {
        setPrincipalAgent(d);
        if (d && r.review_status === 'PENDING' && m !== 'VIEW') {
          // Default TRUE only when all POBs are clearly PRINCIPAL
          setStep2PaAffirmed(!d.has_agent && !d.has_undetermined);
        }
      })
      .catch(() => setPrincipalAgent(null))
      .finally(() => setPrincipalAgentLoading(false));
    // Phase 9a — variable consideration state reset + fetch
    setVariableConsideration(null);
    setVariableConsiderationError(null);
    setStep3VcAffirmed(!!r.step3_variable_consideration_affirmed);
    setStep3VcNotes(r.step3_variable_consideration_notes || '');
    // Phase 9b — refund liabilities for the contract
    setRefundLiabilities([]);
    authFetch(`${API_V1}/revenue/refund-liabilities?contract_id=${r.revenue_contract_id}`)
      .then(res => res.ok ? res.json() : null)
      .then((d: { liabilities?: RefundLiability[] } | null) => {
        setRefundLiabilities(d?.liabilities || []);
      })
      .catch(() => setRefundLiabilities([]));
    // Phase 9d — escalation history
    setEscalationHistory([]);
    setEscalationDelta('');
    setEscalationDate('');
    setEscalationDesc('');
    setEscalationError(null);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/escalation-history`)
      .then(res => res.ok ? res.json() : null)
      .then((d: { history?: EscalationEvent[] } | null) => {
        setEscalationHistory(d?.history || []);
      })
      .catch(() => setEscalationHistory([]));
    // Phase 9e — CPS list
    setCpsRows([]);
    setStep3CpsAffirmed(!!r.step3_cps_affirmed);
    setStep3CpsNotes(r.step3_cps_notes || '');
    // ASC 606 Phase 10e — Step 4 state population
    setStep4AllocAffirmed(!!r.step4_allocation_affirmed);
    setStep4AllocNotes(r.step4_allocation_notes || '');
    setStep4SdAffirmed(!!r.step4_specific_discount_affirmed);
    setStep4SdNotes(r.step4_specific_discount_notes || '');
    setStep4SdCrit1(!!r.step4_sd_criterion_standalone);
    setStep4SdCrit2(!!r.step4_sd_criterion_bundle_discount);
    setStep4SdCrit3(!!r.step4_sd_criterion_observable_evidence);
    setStep4VcSpecAffirmed(!!r.step4_vc_specific_affirmed);
    setStep4VcSpecNotes(r.step4_vc_specific_notes || '');
    setStep4VcCrit1(!!r.step4_vc_criterion_terms_specific);
    setStep4VcCrit2(!!r.step4_vc_criterion_faithful_depiction);
    setStep4ResAttested(!!r.step4_residual_attested);
    setStep4ResNotes(r.step4_residual_notes || '');
    setStep4ResCondition(r.step4_residual_condition || '');
    // Phase 11f — populate Step 5 state from review row
    setStep5OtAffirmed(!!r.step5_over_time_qualified_affirmed);
    setStep5OtNotes(r.step5_over_time_qualified_notes || '');
    setStep5OtCritA(r.step5_over_time_criterion_a_simultaneous_consumption);
    setStep5OtCritB(r.step5_over_time_criterion_b_customer_controls);
    setStep5OtCritC(r.step5_over_time_criterion_c_no_alternative_use_and_right_to_payment);
    setStep5PmAffirmed(!!r.step5_progress_method_affirmed);
    setStep5PmNotes(r.step5_progress_method_notes || '');
    setStep5CostBaselineAffirmed(!!r.step5_cost_baseline_affirmed);
    setStep5CostBaselineNotes(r.step5_cost_baseline_notes || '');
    setStep5UninstalledAffirmed(!!r.step5_uninstalled_materials_carved_out_affirmed);
    setStep5UninstalledNotes(r.step5_uninstalled_materials_carved_out_notes || '');
    setStep5MilestonesAffirmed(!!r.step5_milestones_reasonable_measure_affirmed);
    setStep5MilestonesNotes(r.step5_milestones_reasonable_measure_notes || '');
    setStep5UnitsAffirmed(!!r.step5_units_carve_out_affirmed);
    setStep5UnitsNotes(r.step5_units_carve_out_notes || '');
    setStep5R2iAffirmed(!!r.step5_r2i_expedient_affirmed);
    setStep5R2iNotes(r.step5_r2i_expedient_notes || '');
    setStep5OnerousAffirmed(!!r.step5_onerous_contract_reviewed);
    setStep5OnerousNotes(r.step5_onerous_contract_notes || '');
    // Phase 11g — Step 5 pre-flight: fetch contract POBs + satisfaction state + onerous actions
    setStep5PreflightBlocks([]);
    setStep5HasOverTimePob(false);
    setStep5HasCostToCost(false);
    setStep5HasMilestones(false);
    setStep5HasUnits(false);
    setStep5HasR2i(false);
    setStep5HasPendingRevision(false);
    setStep5HasOpenOnerous(false);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}`)
      .then(res => res.ok ? res.json() : null)
      .then((rc: any) => {
        if (!rc) return;
        const obs = rc.obligations || [];
        const UNSUPPORTED = new Set(['LABOR_HOURS', 'OUTPUT_SURVEY']);
        const overTime = obs.filter((p: any) => {
          const m = (p.progress_method || '').toUpperCase();
          return m && m !== 'POINT_IN_TIME' && m !== 'NONE';
        });
        const blocks: string[] = [];
        if (obs.some((p: any) => Number(p.pending_estimate_revision_amount || 0) !== 0)) {
          blocks.push('STEP5_PENDING_ESTIMATE_REVISION_UNRESOLVED');
          setStep5HasPendingRevision(true);
        }
        const unsupportedList = overTime.filter((p: any) =>
          UNSUPPORTED.has((p.progress_method || '').toUpperCase())
        );
        if (unsupportedList.length > 0) {
          blocks.push('STEP5_UNSUPPORTED_PROGRESS_METHOD');
        }
        const classifierMissingList = overTime.filter((p: any) => p.over_time_qualified == null);
        if (classifierMissingList.length > 0) {
          blocks.push('STEP5_CLASSIFIER_NOT_RUN');
        }
        setStep5PreflightBlocks(blocks);
        setStep5HasOverTimePob(overTime.length > 0);
        setStep5HasCostToCost(overTime.some((p: any) => (p.progress_method || '').toUpperCase() === 'COST_TO_COST'));
        setStep5HasMilestones(overTime.some((p: any) => (p.progress_method || '').toUpperCase() === 'MILESTONES_COMPLETED'));
        setStep5HasUnits(overTime.some((p: any) => (p.progress_method || '').toUpperCase() === 'UNITS_DELIVERED'));
        setStep5HasR2i(obs.some((p: any) => !!p.right_to_invoice_expedient));
      })
      .catch(() => {
        // non-fatal — backend will enforce
      });
    // ASC 340-40 Phase 12d — populate Step 6 state from review row
    setStep6CcReviewed(!!r.step6_contract_costs_reviewed_affirmed);
    setStep6CcReviewedNotes(r.step6_contract_costs_reviewed_notes || '');
    setStep6Recoverable(!!r.step6_recoverable_affirmed);
    setStep6RecoverableNotes(r.step6_recoverable_notes || '');
    setStep6AmortMethod(!!r.step6_amortization_method_affirmed);
    setStep6AmortMethodNotes(r.step6_amortization_method_notes || '');
    setStep6ImpTested(!!r.step6_impairment_tested_affirmed);
    setStep6ImpTestedNotes(r.step6_impairment_tested_notes || '');
    setStep6Expedient(!!r.step6_25_4_expedient_election_affirmed);
    setStep6ExpedientNotes(r.step6_25_4_expedient_election_notes || '');
    // Phase 12d — Step 6 pre-flight: fetch cost assets + impairment history
    setStep6PreflightBlocks([]);
    setStep6HasAnyCostAsset(false);
    setStep6HasCapitalizedAsset(false);
    setStep6HasExpedientAsset(false);
    setStep6HasImpairmentHistory(false);
    Promise.all([
      authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/acquisition-costs?include_inactive=true&limit=500`)
        .then(res => res.ok ? res.json() : { assets: [] })
        .catch(() => ({ assets: [] })),
      authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/impairment-history?limit=500`)
        .then(res => res.ok ? res.json() : { history: [] })
        .catch(() => ({ history: [] })),
    ]).then(([costRes, impRes]: any[]) => {
      const assets = costRes.assets || [];
      const history = impRes.history || [];
      const blocks: string[] = [];
      if (history.some((h: any) => (h.status || '').toUpperCase() === 'PENDING')) {
        blocks.push('STEP6_PENDING_IMPAIRMENT_UNRESOLVED');
      }
      if (history.some((h: any) => (h.status || '').toUpperCase() === 'FAILED')) {
        blocks.push('STEP6_FAILED_IMPAIRMENT_REQUIRES_REVIEW');
      }
      if (assets.some((a: any) => (a.status || '').toUpperCase() === 'IMPAIRED')) {
        blocks.push('STEP6_COST_ASSET_IMPAIRED_NOT_CONSUMED');
      }
      setStep6PreflightBlocks(blocks);
      setStep6HasAnyCostAsset(assets.length > 0);
      setStep6HasCapitalizedAsset(assets.some((a: any) => {
        const s = (a.status || '').toUpperCase();
        return s !== 'EXPENSED_25_4' && s !== 'REVERSED';
      }));
      setStep6HasExpedientAsset(assets.some((a: any) => (a.status || '').toUpperCase() === 'EXPENSED_25_4'));
      setStep6HasImpairmentHistory(history.some((h: any) => {
        const s = (h.status || '').toUpperCase();
        return s !== 'PENDING' && s !== 'FAILED';
      }));
    }).catch(() => { /* non-fatal; backend enforces */ });
    setCpsForm({
      payment_type: 'REBATE',
      classification: 'TRANSACTION_PRICE_REDUCTION',
      description: '',
      gross_amount: '',
      distinct_service_fair_value: '',
    });
    setCpsError(null);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/cps`)
      .then(res => res.ok ? res.json() : null)
      .then((d: { payments?: CPSRow[] } | null) => {
        const rows = d?.payments || [];
        setCpsRows(rows);
        const hasActive = rows.some(p => p.status === 'OPEN' || p.status === 'ACCRUED');
        if (r.review_status === 'PENDING' && m !== 'VIEW') {
          setStep3CpsAffirmed(!hasActive);
        }
      })
      .catch(() => setCpsRows([]));
    // Phase 9c — financing + noncash
    setFinancing(null);
    setFinancingError(null);
    setStep3FinancingAffirmed(!!r.step3_financing_affirmed);
    setStep3FinancingNotes(r.step3_financing_notes || '');
    setFinancingLoading(true);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/financing-result`)
      .then(res => res.ok ? res.json() : null)
      .then(async (d: FinancingResult | null) => {
        if (!d) return null;
        const needsAssess =
          m !== 'VIEW' &&
          r.review_status === 'PENDING' &&
          !d.applies_practical_expedient &&
          d.rate_source === 'N/A';
        if (!needsAssess) return d;
        const runRes = await authFetch(
          `${CONTRACTS_API}/${r.revenue_contract_id}/assess-financing`,
          { method: 'POST' },
        );
        if (runRes.ok) return (await runRes.json()) as FinancingResult;
        if (runRes.status === 409) {
          const body = await runRes.json().catch(() => null);
          setFinancingError(
            body?.detail?.message ||
            'Tenant configuration required: set FINANCING_SIGNIFICANCE_THRESHOLD and IMPUTED_DISCOUNT_RATE in Client Policies.'
          );
        }
        return d;
      })
      .then((d: FinancingResult | null) => {
        setFinancing(d);
        if (d && r.review_status === 'PENDING' && m !== 'VIEW') {
          setStep3FinancingAffirmed(!d.significant);
        }
      })
      .catch(() => setFinancing(null))
      .finally(() => setFinancingLoading(false));
    setNoncash(null);
    setStep3NoncashAffirmed(!!r.step3_noncash_affirmed);
    setStep3NoncashNotes(r.step3_noncash_notes || '');
    setNoncashFvInput(r.noncash_consideration_fv ? String(r.noncash_consideration_fv) : '');
    setNoncashDescInput(r.noncash_description || '');
    setNoncashLoading(true);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/noncash-result`)
      .then(res => res.ok ? res.json() : null)
      .then((d: NoncashResult | null) => {
        setNoncash(d);
        if (d && r.review_status === 'PENDING' && m !== 'VIEW') {
          setStep3NoncashAffirmed(!d.detected);
        }
      })
      .catch(() => setNoncash(null))
      .finally(() => setNoncashLoading(false));
    setVariableConsiderationLoading(true);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/variable-consideration-result`)
      .then(res => res.ok ? res.json() : null)
      .then(async (d: VariableConsiderationResult | null) => {
        if (!d) return null;
        const needsAssess =
          m !== 'VIEW' &&
          r.review_status === 'PENDING' &&
          !d.has_components &&
          d.assessed_at === null;
        if (!needsAssess) return d;
        const runRes = await authFetch(
          `${CONTRACTS_API}/${r.revenue_contract_id}/assess-variable-consideration`,
          { method: 'POST' },
        );
        if (runRes.ok) return (await runRes.json()) as VariableConsiderationResult;
        if (runRes.status === 409) {
          const body = await runRes.json().catch(() => null);
          setVariableConsiderationError(
            body?.detail?.message ||
            'Tenant configuration required: set CONSTRAINT_PROBABILITY_THRESHOLD in Client Policies.'
          );
        }
        return d;
      })
      .then((d: VariableConsiderationResult | null) => {
        setVariableConsideration(d);
        if (d && r.review_status === 'PENDING' && m !== 'VIEW') {
          setStep3VcAffirmed(!d.has_components);
        }
      })
      .catch(() => setVariableConsideration(null))
      .finally(() => setVariableConsiderationLoading(false));
    // Phase 8d — material right state reset + fetch
    setMaterialRight(null);
    setStep2MaterialRightAffirmed(!!r.step2_material_right_affirmed);
    setStep2MaterialRightNotes(r.step2_material_right_notes || '');
    setMaterialRightLoading(true);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/material-right-result`)
      .then(res => res.ok ? res.json() : null)
      .then(async (d: MaterialRightResult | null) => {
        if (!d) return null;
        const needsAssess =
          m !== 'VIEW' &&
          r.review_status === 'PENDING' &&
          d.pool_size > 1 &&
          !d.pobs.some(p => p.classified_at);
        if (!needsAssess) return d;
        const runRes = await authFetch(
          `${CONTRACTS_API}/${r.revenue_contract_id}/assess-material-right`,
          { method: 'POST' },
        );
        if (runRes.ok) return (await runRes.json()) as MaterialRightResult;
        return d;
      })
      .then((d: MaterialRightResult | null) => {
        setMaterialRight(d);
        // Default: affirm TRUE if no SSP-required types; FALSE if any require SSP
        if (d && r.review_status === 'PENDING' && m !== 'VIEW') {
          setStep2MaterialRightAffirmed(!d.requires_ssp_library);
        }
      })
      .catch(() => setMaterialRight(null))
      .finally(() => setMaterialRightLoading(false));
    setWarrantyLoading(true);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/warranty-result`)
      .then(res => res.ok ? res.json() : null)
      .then(async (d: WarrantyResult | null) => {
        if (!d) return null;
        const needsAssess =
          m !== 'VIEW' &&
          r.review_status === 'PENDING' &&
          d.pool_size > 1 &&
          !d.pobs.some(p => p.classified_at);
        if (!needsAssess) return d;
        const runRes = await authFetch(
          `${CONTRACTS_API}/${r.revenue_contract_id}/assess-warranty`,
          { method: 'POST' },
        );
        if (runRes.ok) return (await runRes.json()) as WarrantyResult;
        return d;
      })
      .then((d: WarrantyResult | null) => {
        setWarranty(d);
        if (d && r.review_status === 'PENDING' && m !== 'VIEW') {
          setStep2WarrantyAffirmed(!d.has_service_warranty);
        }
      })
      .catch(() => setWarranty(null))
      .finally(() => setWarrantyLoading(false));
    setDistinctnessLoading(true);
    authFetch(`${CONTRACTS_API}/${r.revenue_contract_id}/distinctness-result`)
      .then(res => res.ok ? res.json() : null)
      .then(async (d: DistinctnessResult | null) => {
        if (!d) return null;
        // If active review (APPROVE/REJECT) and pool > 1 and never assessed, auto-run.
        const needsAssess =
          m !== 'VIEW' &&
          r.review_status === 'PENDING' &&
          d.pool_size > 1 &&
          !d.pobs.some(p => p.assessed_at);
        if (!needsAssess) return d;
        const runRes = await authFetch(
          `${CONTRACTS_API}/${r.revenue_contract_id}/assess-distinctness`,
          { method: 'POST' },
        );
        if (runRes.ok) return (await runRes.json()) as DistinctnessResult;
        return d;
      })
      .then((d: DistinctnessResult | null) => {
        setDistinctness(d);
        if (d) {
          const hasCombine = d.pobs.some(p => p.combine_reason);
          // Default affirmation: TRUE when no combine candidates (formality),
          // FALSE when candidates exist (reviewer must explicitly click).
          // For already-APPROVED reviews we keep the persisted value.
          if (r.review_status === 'PENDING' && m !== 'VIEW') {
            setStep2Affirmed(!hasCombine);
          }
        }
      })
      .catch(() => setDistinctness(null))
      .finally(() => setDistinctnessLoading(false));
  };

  const openCombineDialog = (candidate: any) => {
    setCombineTarget(candidate);
    setCombineReason('');
    setCombineModalOpen(true);
  };

  const submitCombine = async () => {
    if (!selected || !combineTarget || !combineReason.trim()) return;
    setCombineSubmitting(true);
    try {
      // The "primary" is the selected review's contract; the combineTarget becomes secondary
      // Service puts the FK on the secondary pointing at the primary.
      const res = await authFetch(
        `${CONTRACTS_API}/${combineTarget.id}/combine`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            primary_id: selected.revenue_contract_id,
            reason: combineReason.trim(),
          }),
        },
      );
      if (res.ok) {
        setCombineModalOpen(false);
        setCombineTarget(null);
        setCombineReason('');
        // Refresh candidates — combined one should drop off
        if (selected) {
          const r = await authFetch(`${CONTRACTS_API}/${selected.revenue_contract_id}/combination-candidates`);
          if (r.ok) setCandidates((await r.json()).candidates || []);
        }
      } else {
        const err = await res.json().catch(() => ({ detail: 'Failed' }));
        setFormError(err.detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setFormError(e?.message || 'Network error');
    } finally {
      setCombineSubmitting(false);
    }
  };

  const closeModal = () => {
    setSelected(null);
    setFormError(null);
  };

  // Phase 8g — submit lease bifurcation
  const submitBifurcate = async () => {
    if (!selected) return;
    const f = bifurcateForm;
    if (!f.allocated_amount || !f.description.trim() || !f.commencement_date || !f.end_date || !f.monthly_payment) {
      setBifurcateError('Required: allocated amount, description, start date, end date, monthly payment.');
      return;
    }
    setBifurcateSubmitting(true);
    setBifurcateError(null);
    try {
      const body: Record<string, any> = {
        allocated_amount: parseFloat(f.allocated_amount),
        description: f.description.trim(),
        commencement_date: f.commencement_date,
        end_date: f.end_date,
        monthly_payment: parseFloat(f.monthly_payment),
        discount_rate: parseFloat(f.discount_rate || '0.05'),
        framework: f.framework,
        classification: f.classification,
      };
      if (f.asset_type) body.asset_type = f.asset_type;
      if (f.lessor_name.trim()) body.lessor_name = f.lessor_name.trim();
      if (f.basis_note.trim()) body.basis_note = f.basis_note.trim();
      const res = await authFetch(`${CONTRACTS_API}/${selected.revenue_contract_id}/bifurcate-lease`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setBifurcateModalOpen(false);
        // Refresh components + the review (total_value has been reduced)
        const cr = await authFetch(`${CONTRACTS_API}/${selected.revenue_contract_id}/components`);
        if (cr.ok) setComponents(await cr.json());
        await load();
      } else {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        setBifurcateError(err.detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setBifurcateError(e?.message || 'Network error');
    } finally {
      setBifurcateSubmitting(false);
    }
  };

  // Phase 8f — submit merge POBs
  const submitMerge = async () => {
    if (!selected || !mergeGroup || !mergeJustification.trim()) return;
    setMergeSubmitting(true);
    setMergeError(null);
    try {
      const body: Record<string, any> = {
        pob_ids: mergeGroup.members.map(m => m.pob_id),
        justification: mergeJustification.trim(),
      };
      if (mergeForcedMethod) body.forced_method = mergeForcedMethod;
      const res = await authFetch(`${API_V1}/revenue/pobs/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setMergeModalOpen(false);
        setMergeGroup(null);
        setMergeJustification('');
        setMergeForcedMethod('');
        // Refresh distinctness results — merged children drop out of merge groups
        const rr = await authFetch(`${CONTRACTS_API}/${selected.revenue_contract_id}/distinctness-result`);
        if (rr.ok) setDistinctness(await rr.json());
      } else {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        setMergeError(err.detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setMergeError(e?.message || 'Network error');
    } finally {
      setMergeSubmitting(false);
    }
  };

  const submitCps = async () => {
    if (!selected) return;
    if (!cpsForm.description.trim()) {
      setCpsError('Description is required');
      return;
    }
    const gross = parseFloat(cpsForm.gross_amount);
    if (!Number.isFinite(gross) || gross <= 0) {
      setCpsError('Gross amount must be > 0');
      return;
    }
    let fv: number | null = null;
    if (cpsForm.classification === 'DISTINCT_SERVICE_RECEIVED') {
      fv = parseFloat(cpsForm.distinct_service_fair_value);
      if (!Number.isFinite(fv) || fv < 0) {
        setCpsError('Fair value is required (≥ 0) for DISTINCT_SERVICE_RECEIVED branch');
        return;
      }
    }
    setCpsSubmitting(true);
    setCpsError(null);
    try {
      const body: Record<string, any> = {
        payment_type: cpsForm.payment_type,
        classification: cpsForm.classification,
        description: cpsForm.description.trim(),
        gross_amount: gross,
      };
      if (fv !== null) body.distinct_service_fair_value = fv;
      const res = await authFetch(
        `${CONTRACTS_API}/${selected.revenue_contract_id}/cps`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (res.ok) {
        setCpsForm({
          payment_type: 'REBATE',
          classification: 'TRANSACTION_PRICE_REDUCTION',
          description: '',
          gross_amount: '',
          distinct_service_fair_value: '',
        });
        const listRes = await authFetch(`${CONTRACTS_API}/${selected.revenue_contract_id}/cps`);
        if (listRes.ok) setCpsRows((await listRes.json()).payments || []);
      } else {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        setCpsError(err.detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setCpsError(e?.message || 'Network error');
    } finally {
      setCpsSubmitting(false);
    }
  };

  const cpsAction = async (cpsId: number, action: 'accrue' | 'settle' | 'release', body?: Record<string, any>) => {
    if (!selected) return;
    try {
      const res = await authFetch(`${API_V1}/revenue/cps/${cpsId}/${action}`, {
        method: 'POST',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.ok) {
        const listRes = await authFetch(`${CONTRACTS_API}/${selected.revenue_contract_id}/cps`);
        if (listRes.ok) setCpsRows((await listRes.json()).payments || []);
      } else {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        setCpsError(err.detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setCpsError(e?.message || 'Network error');
    }
  };

  const submitEscalation = async () => {
    if (!selected) return;
    const delta = parseFloat(escalationDelta);
    if (!escalationDelta || !Number.isFinite(delta) || delta === 0) {
      setEscalationError('Delta must be a non-zero number');
      return;
    }
    if (!escalationDate) {
      setEscalationError('Effective date is required');
      return;
    }
    if (!escalationDesc.trim()) {
      setEscalationError('Description is required (ASC 606-10-32-14 audit trail)');
      return;
    }
    setEscalationSubmitting(true);
    setEscalationError(null);
    try {
      const res = await authFetch(
        `${CONTRACTS_API}/${selected.revenue_contract_id}/apply-escalation`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            delta_amount: delta,
            effective_date: escalationDate,
            description: escalationDesc.trim(),
          }),
        },
      );
      if (res.ok) {
        setEscalationDelta('');
        setEscalationDate('');
        setEscalationDesc('');
        // Refresh history + variable consideration
        const histRes = await authFetch(`${CONTRACTS_API}/${selected.revenue_contract_id}/escalation-history`);
        if (histRes.ok) setEscalationHistory((await histRes.json()).history || []);
        const vcRes = await authFetch(`${CONTRACTS_API}/${selected.revenue_contract_id}/variable-consideration-result`);
        if (vcRes.ok) setVariableConsideration(await vcRes.json());
      } else {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        setEscalationError(err.detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setEscalationError(e?.message || 'Network error');
    } finally {
      setEscalationSubmitting(false);
    }
  };

  const allCriteriaTrue = ['1', '2', '3', '4', '5'].every(k => criteriaValues[k]?.value === true);
  const isEvergreen = selected?.term_type === 'EVERGREEN';
  const termValid = !isEvergreen || (parseInt(enforceableTerm, 10) > 0);
  // Phase 8b — Step 2 gate: approve blocked when combine candidates exist and reviewer hasn't affirmed
  const step2HasCombineCandidates = !!distinctness?.pobs.some(p => p.combine_reason);
  const step2GateOk = !step2HasCombineCandidates || step2Affirmed;
  // Phase 8c — warranty gate: approve blocked when any SERVICE warranty exists unaffirmed
  const step2HasServiceWarranty = !!warranty?.has_service_warranty;
  const step2WarrantyGateOk = !step2HasServiceWarranty || step2WarrantyAffirmed;
  // Phase 8d — material right gate: approve blocked when any SSP-required type is unaffirmed
  const step2MrRequiresSsp = !!materialRight?.requires_ssp_library;
  const step2MaterialRightGateOk = !step2MrRequiresSsp || step2MaterialRightAffirmed;
  // Phase 8e — principal-vs-agent gate: approve blocked when any AGENT or UNDETERMINED POB is unaffirmed
  const step2PaNeedsReview = !!(principalAgent?.has_agent || principalAgent?.has_undetermined);
  const step2PaGateOk = !step2PaNeedsReview || step2PaAffirmed;
  // Phase 9a — Step 3 variable consideration gate: approve blocked when any
  // ACTIVE component exists and reviewer hasn't affirmed, OR when any component
  // remains unquantified (estimated=0 AND probability=null).
  const step3HasComponents = !!variableConsideration?.has_components;
  const step3HasUnquantified = !!variableConsideration?.has_unquantified_components;
  const step3VcGateOk = (!step3HasComponents || step3VcAffirmed) && !step3HasUnquantified;
  // Phase 9c — Step 3 financing gate
  const step3FinancingSignificant = !!financing?.significant;
  const step3FinancingGateOk = !step3FinancingSignificant || step3FinancingAffirmed;
  // Phase 9c — Step 3 noncash gate
  const step3NoncashDetected = !!noncash?.detected;
  const step3NoncashFvRecorded = !!noncash?.fv_recorded || (!!noncashFvInput && !!noncashDescInput.trim());
  const step3NoncashGateOk = !step3NoncashDetected || (step3NoncashFvRecorded && step3NoncashAffirmed);
  // Phase 9e — Step 3 CPS gate
  const step3CpsHasActive = cpsRows.some(p => p.status === 'OPEN' || p.status === 'ACCRUED');
  const step3CpsGateOk = !step3CpsHasActive || step3CpsAffirmed;
  // Phase 11f — Step 5 pre-flight gate: always-required attestations + conditional by method
  const step5OtCritsComplete =
    !step5HasOverTimePob
    || (step5OtCritA !== null && step5OtCritB !== null && step5OtCritC !== null);
  const step5AlwaysOk =
    !step5HasOverTimePob
    || (step5OtAffirmed && step5PmAffirmed && step5OtCritsComplete);
  const step5CostOk = !step5HasCostToCost || (step5CostBaselineAffirmed && step5UninstalledAffirmed);
  const step5MilestonesOk = !step5HasMilestones || step5MilestonesAffirmed;
  const step5UnitsOk = !step5HasUnits || step5UnitsAffirmed;
  const step5R2iOk = !step5HasR2i || step5R2iAffirmed;
  const step5OnerousOk = !step5HasOpenOnerous || step5OnerousAffirmed;
  const step5GateOk =
    !step5HasPendingRevision
    && step5PreflightBlocks.length === 0
    && step5AlwaysOk
    && step5CostOk
    && step5MilestonesOk
    && step5UnitsOk
    && step5R2iOk
    && step5OnerousOk;
  // Phase 12d — Step 6 composite gate
  const step6AlwaysOk = !step6HasAnyCostAsset || (step6CcReviewed && step6Recoverable);
  const step6AmortMethodOk = !step6HasCapitalizedAsset || step6AmortMethod;
  const step6ImpTestedOk = !step6HasImpairmentHistory || step6ImpTested;
  const step6ExpedientOk = !step6HasExpedientAsset || step6Expedient;
  const step6GateOk =
    step6PreflightBlocks.length === 0
    && step6AlwaysOk
    && step6AmortMethodOk
    && step6ImpTestedOk
    && step6ExpedientOk;
  const approveOk = allCriteriaTrue && collectibilityStatus === 'PROBABLE' && termValid && step2GateOk && step2WarrantyGateOk && step2MaterialRightGateOk && step2PaGateOk && step3VcGateOk && step3FinancingGateOk && step3NoncashGateOk && step3CpsGateOk && step5GateOk && step6GateOk;

  const submitApprove = async () => {
    if (!selected) return;
    if (!allCriteriaTrue) {
      setFormError('All 5 criteria must be affirmative to approve. Reject instead if any criterion fails.');
      return;
    }
    if (collectibilityStatus !== 'PROBABLE') {
      setFormError('Collectibility must be PROBABLE to approve per ASC 606-10-25-1(e).');
      return;
    }
    if (isEvergreen && !termValid) {
      setFormError('Enforceable term (months) is required for EVERGREEN contracts per ASC 606-10-25-3.');
      return;
    }
    if (!step2GateOk) {
      setFormError('Step 2 distinctness has combine candidates — reviewer must affirm the assessment before approval (ASC 606-10-25-14 to 25-22).');
      return;
    }
    if (!step2WarrantyGateOk) {
      setFormError('Step 2 warranty has SERVICE-type coverage — reviewer must affirm before approval (ASC 606-10-55-30 to 55-35).');
      return;
    }
    if (!step2MaterialRightGateOk) {
      setFormError('Step 2 material right requires Phase 10 SSP library — reviewer must affirm the dependency before approval (ASC 606-10-55-41 to 55-45).');
      return;
    }
    if (!step2PaGateOk) {
      setFormError('Step 2 principal-vs-agent has AGENT or UNDETERMINED POBs — reviewer must affirm before approval (ASC 606-10-55-36 to 55-40).');
      return;
    }
    if (step3HasUnquantified) {
      setFormError('Step 3 variable consideration has unquantified component(s) — supply estimated amount and probability before approval (ASC 606-10-32-8).');
      return;
    }
    if (!step3VcGateOk) {
      setFormError('Step 3 variable consideration has active component(s) — reviewer must affirm before approval (ASC 606-10-32-5 to 32-13).');
      return;
    }
    if (!step3FinancingGateOk) {
      setFormError('Step 3 significant financing component detected — reviewer must affirm before approval (ASC 606-10-32-15 to 32-20).');
      return;
    }
    if (step3NoncashDetected && !step3NoncashFvRecorded) {
      setFormError('Step 3 noncash consideration detected — supply inception fair value and description before approval (ASC 606-10-32-22).');
      return;
    }
    if (!step3NoncashGateOk) {
      setFormError('Step 3 noncash consideration requires reviewer affirmation (ASC 606-10-32-21 to 32-24).');
      return;
    }
    if (!step3CpsGateOk) {
      setFormError('Step 3 consideration payable to customer has active row(s) — reviewer must affirm classification (ASC 606-10-32-25 to 32-27).');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const body: Record<string, any> = {
        criterion_1_approval: criteriaValues['1']?.value === true,
        criterion_2_rights: criteriaValues['2']?.value === true,
        criterion_3_payment: criteriaValues['3']?.value === true,
        criterion_4_commercial: criteriaValues['4']?.value === true,
        criterion_5_collectibility: criteriaValues['5']?.value === true,
        criterion_1_notes: criteriaValues['1']?.notes || null,
        criterion_2_notes: criteriaValues['2']?.notes || null,
        criterion_3_notes: criteriaValues['3']?.notes || null,
        criterion_4_notes: criteriaValues['4']?.notes || null,
        criterion_5_notes: criteriaValues['5']?.notes || null,
        collectibility_status: collectibilityStatus,
        collectibility_notes: collectibilityNotes || null,
      };
      if (isEvergreen) {
        body.enforceable_term_months = parseInt(enforceableTerm, 10);
      }
      // Phase 8b — Step 2 distinctness affirmation
      body.step2_distinctness_affirmed = step2Affirmed;
      body.step2_distinctness_notes = step2Notes.trim() || null;
      // Phase 8c — Step 2 warranty affirmation
      body.step2_warranty_affirmed = step2WarrantyAffirmed;
      body.step2_warranty_notes = step2WarrantyNotes.trim() || null;
      // Phase 8d — Step 2 material right affirmation
      body.step2_material_right_affirmed = step2MaterialRightAffirmed;
      body.step2_material_right_notes = step2MaterialRightNotes.trim() || null;
      // Phase 8e — Step 2 principal-vs-agent affirmation
      body.step2_principal_agent_affirmed = step2PaAffirmed;
      body.step2_principal_agent_notes = step2PaNotes.trim() || null;
      // Phase 9a — Step 3 variable consideration affirmation
      body.step3_variable_consideration_affirmed = step3VcAffirmed;
      body.step3_variable_consideration_notes = step3VcNotes.trim() || null;
      // Phase 9c — Step 3 financing affirmation
      body.step3_financing_affirmed = step3FinancingAffirmed;
      body.step3_financing_notes = step3FinancingNotes.trim() || null;
      // Phase 9c — Step 3 noncash affirmation
      body.step3_noncash_affirmed = step3NoncashAffirmed;
      body.step3_noncash_notes = step3NoncashNotes.trim() || null;
      if (noncashFvInput && noncashDescInput.trim()) {
        body.noncash_fv = parseFloat(noncashFvInput);
        body.noncash_description = noncashDescInput.trim();
      }
      // Phase 9e — Step 3 CPS affirmation
      body.step3_cps_affirmed = step3CpsAffirmed;
      body.step3_cps_notes = step3CpsNotes.trim() || null;
      // Phase 10e — Step 4 allocation attestations
      body.step4_allocation_affirmed = step4AllocAffirmed;
      body.step4_allocation_notes = step4AllocNotes.trim() || null;
      body.step4_specific_discount_affirmed = step4SdAffirmed;
      body.step4_specific_discount_notes = step4SdNotes.trim() || null;
      body.step4_sd_criterion_standalone = step4SdCrit1;
      body.step4_sd_criterion_bundle_discount = step4SdCrit2;
      body.step4_sd_criterion_observable_evidence = step4SdCrit3;
      body.step4_vc_specific_affirmed = step4VcSpecAffirmed;
      body.step4_vc_specific_notes = step4VcSpecNotes.trim() || null;
      body.step4_vc_criterion_terms_specific = step4VcCrit1;
      body.step4_vc_criterion_faithful_depiction = step4VcCrit2;
      body.step4_residual_attested = step4ResAttested;
      body.step4_residual_notes = step4ResNotes.trim() || null;
      body.step4_residual_condition = step4ResCondition || null;
      // Phase 11f — Step 5 attestations
      body.step5_over_time_qualified_affirmed = step5OtAffirmed;
      body.step5_over_time_qualified_notes = step5OtNotes.trim() || null;
      body.step5_over_time_criterion_a_simultaneous_consumption = step5OtCritA;
      body.step5_over_time_criterion_b_customer_controls = step5OtCritB;
      body.step5_over_time_criterion_c_no_alternative_use_and_right_to_payment = step5OtCritC;
      body.step5_progress_method_affirmed = step5PmAffirmed;
      body.step5_progress_method_notes = step5PmNotes.trim() || null;
      body.step5_cost_baseline_affirmed = step5CostBaselineAffirmed;
      body.step5_cost_baseline_notes = step5CostBaselineNotes.trim() || null;
      body.step5_uninstalled_materials_carved_out_affirmed = step5UninstalledAffirmed;
      body.step5_uninstalled_materials_carved_out_notes = step5UninstalledNotes.trim() || null;
      body.step5_milestones_reasonable_measure_affirmed = step5MilestonesAffirmed;
      body.step5_milestones_reasonable_measure_notes = step5MilestonesNotes.trim() || null;
      body.step5_units_carve_out_affirmed = step5UnitsAffirmed;
      body.step5_units_carve_out_notes = step5UnitsNotes.trim() || null;
      body.step5_r2i_expedient_affirmed = step5R2iAffirmed;
      body.step5_r2i_expedient_notes = step5R2iNotes.trim() || null;
      body.step5_onerous_contract_reviewed = step5OnerousAffirmed;
      body.step5_onerous_contract_notes = step5OnerousNotes.trim() || null;
      // Phase 12d — Step 6 attestations
      body.step6_contract_costs_reviewed_affirmed = step6CcReviewed;
      body.step6_contract_costs_reviewed_notes = step6CcReviewedNotes.trim() || null;
      body.step6_recoverable_affirmed = step6Recoverable;
      body.step6_recoverable_notes = step6RecoverableNotes.trim() || null;
      body.step6_amortization_method_affirmed = step6AmortMethod;
      body.step6_amortization_method_notes = step6AmortMethodNotes.trim() || null;
      body.step6_impairment_tested_affirmed = step6ImpTested;
      body.step6_impairment_tested_notes = step6ImpTestedNotes.trim() || null;
      body.step6_25_4_expedient_election_affirmed = step6Expedient;
      body.step6_25_4_expedient_election_notes = step6ExpedientNotes.trim() || null;
      const res = await authFetch(`${API}/${selected.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        closeModal();
        await load();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        setFormError(err.detail || `Request failed: HTTP ${res.status}`);
      }
    } catch (e: any) {
      setFormError(e?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReject = async () => {
    if (!selected) return;
    if (!rejectionReason.trim()) {
      setFormError('Rejection reason is required.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const body = {
        rejection_reason: rejectionReason.trim(),
        criterion_1_approval: criteriaValues['1']?.value ?? null,
        criterion_2_rights: criteriaValues['2']?.value ?? null,
        criterion_3_payment: criteriaValues['3']?.value ?? null,
        criterion_4_commercial: criteriaValues['4']?.value ?? null,
        criterion_5_collectibility: criteriaValues['5']?.value ?? null,
        criterion_1_notes: criteriaValues['1']?.notes || null,
        criterion_2_notes: criteriaValues['2']?.notes || null,
        criterion_3_notes: criteriaValues['3']?.notes || null,
        criterion_4_notes: criteriaValues['4']?.notes || null,
        criterion_5_notes: criteriaValues['5']?.notes || null,
        collectibility_status: collectibilityStatus,
        collectibility_notes: collectibilityNotes || null,
      };
      const res = await authFetch(`${API}/${selected.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        closeModal();
        await load();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        setFormError(err.detail || `Request failed: HTTP ${res.status}`);
      }
    } catch (e: any) {
      setFormError(e?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const cols: Column[] = [
    { key: 'contract_number', label: 'Contract', render: (v: string) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'customer_name', label: 'Customer', render: (v: string) => v || '-' },
    { key: 'entity_code', label: 'Entity' },
    { key: 'term_type', label: 'Term', render: (v: string) => (
      <span style={{
        display: 'inline-block', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
        background: v === 'EVERGREEN' ? 'rgba(234,179,8,0.15)' : 'transparent',
        color: v === 'EVERGREEN' ? 'var(--warning)' : 'var(--text-secondary)',
        border: v === 'EVERGREEN' ? '1px solid var(--warning)' : '1px solid var(--border-subtle)',
      }}>{v || 'FIXED'}</span>
    ) },
    { key: 'total_value', label: 'Total Value', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'review_status', label: 'Status', render: (v: string) => statusBadge(v) },
    { key: 'source', label: 'Source', render: (v: string) => sourceBadge(v) },
    { key: 'created_at', label: 'Created', render: (v: string) => v ? new Date(v).toLocaleDateString() : '-' },
    {
      key: 'id', label: '', align: 'right',
      render: (_: any, row: Review) => {
        if (row.review_status === 'PENDING') {
          return (
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button
                onClick={() => openReview(row, 'APPROVE')}
                style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: 'var(--positive)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >Review</button>
              <button
                onClick={() => openReview(row, 'REJECT')}
                style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: 'transparent', color: 'var(--negative)', border: '1px solid var(--negative)', borderRadius: 4, cursor: 'pointer' }}
              >Reject</button>
            </div>
          );
        }
        return (
          <button
            onClick={() => openReview(row, 'VIEW')}
            style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 4, cursor: 'pointer' }}
          >View</button>
        );
      },
    },
  ];

  const tabs = [
    { key: 'PENDING', label: `Pending (${allCounts.pending})` },
    { key: 'APPROVED', label: `Approved (${allCounts.approved})` },
    { key: 'REJECTED', label: `Rejected (${allCounts.rejected})` },
    { key: 'ALL', label: 'All' },
  ];

  return (
    <PageLayout
      title="Contract Reviews"
      subtitle="ASC 606 Step 1 — reviewer gate for new revenue contracts"
    >
      <KpiGrid columns={3} items={[
        { label: 'Pending Review', value: allCounts.pending, color: allCounts.pending > 0 ? 'var(--warning)' : 'var(--text-secondary)' },
        { label: 'Approved', value: allCounts.approved, color: 'var(--positive)' },
        { label: 'Rejected', value: allCounts.rejected, color: allCounts.rejected > 0 ? 'var(--negative)' : 'var(--text-secondary)' },
      ]} />

      <TabBar tabs={tabs} active={tab} onChange={(k: string) => setTab(k as any)} />

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading…</div>
      ) : (
        <DataTable
          columns={cols}
          rows={reviews}
          rowKey="id"
          emptyMessage={tab === 'PENDING' ? 'No pending reviews. New contract uploads will appear here.' : 'No reviews match this filter.'}
        />
      )}

      {/* Review Modal */}
      <Modal
        open={!!selected}
        onClose={closeModal}
        title={
          selected
            ? mode === 'VIEW'
              ? `Review: ${selected.contract_number} (${selected.review_status})`
              : mode === 'APPROVE'
                ? `ASC 606 Step 1 Review — ${selected.contract_number}`
                : `Reject Review — ${selected.contract_number}`
            : ''
        }
        description={
          selected
            ? `${selected.customer_name || 'Unknown customer'} · ${selected.entity_code || 'UNASSIGNED'} · ${formatAmount(selected.total_value)} ${selected.currency || ''}`
            : undefined
        }
        size="lg"
        footer={
          selected && mode !== 'VIEW' && (
            <>
              <button
                onClick={closeModal}
                disabled={submitting}
                style={{ padding: '8px 16px', fontSize: 13, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 4, cursor: 'pointer' }}
              >Cancel</button>
              {mode === 'APPROVE' ? (
                <button
                  onClick={submitApprove}
                  disabled={submitting || !approveOk}
                  style={{
                    padding: '8px 16px', fontSize: 13, fontWeight: 600,
                    background: approveOk ? 'var(--positive)' : 'var(--bg-hover)',
                    color: approveOk ? 'white' : 'var(--text-hint)',
                    border: 'none', borderRadius: 4,
                    cursor: submitting ? 'wait' : (approveOk ? 'pointer' : 'not-allowed'),
                  }}
                >{submitting ? 'Approving…' : 'Approve Contract'}</button>
              ) : (
                <button
                  onClick={submitReject}
                  disabled={submitting || !rejectionReason.trim()}
                  style={{
                    padding: '8px 16px', fontSize: 13, fontWeight: 600,
                    background: rejectionReason.trim() ? 'var(--negative)' : 'var(--bg-hover)',
                    color: rejectionReason.trim() ? 'white' : 'var(--text-hint)',
                    border: 'none', borderRadius: 4,
                    cursor: submitting ? 'wait' : (rejectionReason.trim() ? 'pointer' : 'not-allowed'),
                  }}
                >{submitting ? 'Rejecting…' : 'Reject Contract'}</button>
              )}
            </>
          )
        }
      >
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {formError && (
              <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)', borderRadius: 4, color: 'var(--negative)', fontSize: 12 }}>
                {formError}
              </div>
            )}

            {mode === 'VIEW' && selected.review_status !== 'PENDING' && (
              <div style={{ padding: 10, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
                {selected.review_status === 'APPROVED' ? 'Approved' : 'Rejected'}
                {selected.reviewed_at ? ` on ${new Date(selected.reviewed_at).toLocaleString()}` : ''}
                {selected.reviewer_id ? ` by reviewer #${selected.reviewer_id}` : ''}
                {selected.source === 'BACKFILL' ? ' (D29 backfill — pre-Phase-7a cascade)' : ''}
                {selected.rejection_reason && (
                  <div style={{ marginTop: 8, color: 'var(--negative)', fontWeight: 600 }}>Reason: {selected.rejection_reason}</div>
                )}
              </div>
            )}

            {selected.term_type === 'EVERGREEN' && (
              <div style={{
                padding: 12,
                background: 'rgba(234,179,8,0.08)',
                border: '1px solid var(--warning)',
                borderRadius: 4,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--warning)', marginBottom: 4 }}>
                  EVERGREEN Contract — Enforceable Term Required
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                  Per ASC 606-10-25-3, the enforceable term is the notice period or the period over
                  which the customer has a substantive right to terminate without penalty. Commit a
                  term in months before approval. The cascade will create the RATABLE POB + schedule
                  + billing rows using this value.
                </div>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600 }}>Enforceable term (months)</span>
                  <input
                    type="number"
                    min={1}
                    max={600}
                    value={enforceableTerm}
                    disabled={mode === 'VIEW'}
                    onChange={(e) => setEnforceableTerm(e.target.value)}
                    placeholder="e.g. 12"
                    style={{
                      width: 200, padding: 6, fontSize: 13,
                      background: 'var(--bg-surface)',
                      border: `1px solid ${(mode !== 'VIEW' && isEvergreen && !termValid) ? 'var(--negative)' : 'var(--border-subtle)'}`,
                      borderRadius: 4, color: 'var(--text-primary)',
                    }}
                  />
                </label>
                {selected.enforceable_term_months != null && (
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                    Committed: {selected.enforceable_term_months} months
                  </div>
                )}
              </div>
            )}

            {/* Phase 7f — combination candidates (only during APPROVE/REJECT of PENDING reviews) */}
            {mode !== 'VIEW' && selected.review_status === 'PENDING' && (candidates.length > 0 || candidatesLoading) && (
              <div style={{
                padding: 12, border: '1px solid var(--accent)', borderRadius: 6,
                background: 'rgba(59,130,246,0.05)',
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>
                  ASC 606-10-25-9 — Possible Contract Combinations
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                  {candidatesLoading ? 'Checking for near-time contracts with the same customer…' : (
                    `${candidates.length} contract(s) with the same customer and entity signed within ±30 days. ` +
                    'Combine if (a) negotiated as a package, (b) consideration is interdependent, or (c) the goods/services form a single POB.'
                  )}
                </div>
                {!candidatesLoading && candidates.map((c) => (
                  <div key={c.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 10px', marginBottom: 6,
                    background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4,
                    fontSize: 12,
                  }}>
                    <div>
                      <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{c.contract_number}</span>
                      <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>
                        {c.contract_date} · ${Number(c.total_value).toLocaleString()} · {c.status}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => openCombineDialog(c)}
                      style={{
                        padding: '4px 10px', fontSize: 11, fontWeight: 600,
                        background: 'var(--accent)', color: 'white',
                        border: 'none', borderRadius: 4, cursor: 'pointer',
                      }}
                    >Combine…</button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                ASC 606 Step 1 — Contract Existence Criteria (606-10-25-1)
              </div>
              <CriteriaChecklist
                criteria={ASC606_CRITERIA}
                values={criteriaValues}
                onChange={(k, v) => setCriteriaValues(prev => ({ ...prev, [k]: v }))}
                disabled={mode === 'VIEW'}
              />
            </div>

            {/* ASC 606 Phase 8b — Step 2 distinctness section */}
            <div style={{
              padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 6,
              background: step2HasCombineCandidates ? 'rgba(234,179,8,0.05)' : 'var(--bg-surface)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                ASC 606 Step 2 — Distinctness Test (606-10-25-14 to 25-22)
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Each POB must be (a) capable of being distinct — customer can benefit on its own
                or with readily available resources; and (b) separately identifiable — not an
                input to a combined deliverable. When the classifier flags combine candidates,
                reviewer must acknowledge before approval; actual merge executes via Phase 8f
                bundling.
              </div>
              {distinctnessLoading && (
                <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>Running distinctness assessment…</div>
              )}
              {!distinctnessLoading && distinctness && distinctness.pool_size === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  No performance obligations in this contract's pool yet — Step 2 will apply once POBs are materialized.
                </div>
              )}
              {!distinctnessLoading && distinctness && distinctness.pool_size > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {distinctness.pool_size > 1 && distinctness.primary_contract_id !== selected.revenue_contract_id && (
                    <div style={{ fontSize: 11, color: 'var(--accent)' }}>
                      Pool includes combined primary contract #{distinctness.primary_contract_id} (ASC 606-10-25-9)
                    </div>
                  )}
                  {distinctness.pobs.map((p) => (
                    <div key={p.pob_id} style={{
                      padding: 8,
                      border: `1px solid ${p.combine_reason ? 'var(--warning)' : 'var(--border-subtle)'}`,
                      borderRadius: 4,
                      background: p.combine_reason ? 'rgba(234,179,8,0.08)' : 'var(--bg-card)',
                      fontSize: 12,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600 }}>POB #{p.po_number}</span>
                          {p.obligation_name && <span style={{ marginLeft: 6, color: 'var(--text-secondary)' }}>· {p.obligation_name}</span>}
                          {p.description && <div style={{ marginTop: 2, color: 'var(--text-secondary)', fontSize: 11 }}>{p.description}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          <span style={{
                            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                            background: p.capable ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                            color: p.capable ? 'var(--positive)' : 'var(--negative)',
                          }}>{p.capable ? 'capable' : 'not capable'}</span>
                          <span style={{
                            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                            background: p.separable ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)',
                            color: p.separable ? 'var(--positive)' : 'var(--warning)',
                          }}>{p.separable ? 'separable' : 'combine'}</span>
                          {p.combine_reason && (
                            <span style={{
                              padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                              background: 'var(--warning)', color: 'white',
                            }}>{p.combine_reason}</span>
                          )}
                        </div>
                      </div>
                      {p.combine_reason && (
                        <div style={{ marginTop: 4, fontSize: 10, color: 'var(--warning)' }}>
                          {COMBINE_REASON_CITE[p.combine_reason]}
                          {p.matched_phrase && ` — matched "${p.matched_phrase}"`}
                        </div>
                      )}
                    </div>
                  ))}
                  {distinctness.suggested_merge_groups.length > 0 && (
                    <div style={{
                      marginTop: 6, padding: 8, background: 'rgba(234,179,8,0.08)',
                      border: '1px dashed var(--warning)', borderRadius: 4, fontSize: 11,
                    }}>
                      <div style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: 4 }}>
                        Suggested merge groups ({distinctness.suggested_merge_groups.length}):
                      </div>
                      {distinctness.suggested_merge_groups.map((g, i) => (
                        <div key={i} style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <div>
                            <span style={{ fontWeight: 600 }}>{g.combine_reason}:</span>{' '}
                            {g.members.map(m => `#${m.po_number}`).join(', ')}
                          </div>
                          {mode !== 'VIEW' && g.members.length >= 2 && (
                            <button
                              type="button"
                              onClick={() => {
                                setMergeGroup(g);
                                setMergeJustification(`Combine POBs per ASC 606-10-25-21 ${g.combine_reason.toLowerCase()} indicator.`);
                                setMergeForcedMethod('');
                                setMergeError(null);
                                setMergeModalOpen(true);
                              }}
                              style={{
                                padding: '3px 10px', fontSize: 10, fontWeight: 600,
                                background: 'var(--warning)', color: 'white',
                                border: 'none', borderRadius: 3, cursor: 'pointer', flexShrink: 0,
                              }}
                            >Merge POBs…</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {mode !== 'VIEW' && selected.review_status === 'PENDING' && (
                    <div style={{ marginTop: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={step2Affirmed}
                          onChange={(e) => setStep2Affirmed(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <strong>I affirm the Step 2 distinctness assessment.</strong>{' '}
                          {step2HasCombineCandidates
                            ? 'The flagged combine candidates have been reviewed; merges will be executed via Phase 8f bundling if appropriate.'
                            : 'All POBs are distinct; no merges required.'}
                        </span>
                      </label>
                      <textarea
                        value={step2Notes}
                        onChange={(e) => setStep2Notes(e.target.value)}
                        placeholder="Optional: rationale, override notes, or Phase 8f merge plan…"
                        style={{
                          width: '100%', marginTop: 6, padding: 6, fontSize: 11,
                          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                          borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 36,
                          fontFamily: 'inherit',
                        }}
                      />
                      {step2HasCombineCandidates && !step2Affirmed && (
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--negative)' }}>
                          Approval blocked until reviewer affirms the distinctness assessment.
                        </div>
                      )}
                    </div>
                  )}
                  {mode === 'VIEW' && selected.step2_distinctness_affirmed !== null && (
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                      Affirmed: {selected.step2_distinctness_affirmed ? 'Yes' : 'No'}
                      {selected.step2_distinctness_notes && ` · ${selected.step2_distinctness_notes}`}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ASC 606 Phase 8c — Step 2 warranty section */}
            <div style={{
              padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 6,
              background: step2HasServiceWarranty ? 'rgba(234,179,8,0.05)' : 'var(--bg-surface)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                ASC 606 Step 2 — Warranty Classification (606-10-55-30 to 55-35)
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Each POB's warranty is classified as NONE, ASSURANCE (fix-to-specification,
                cost accrual via ASC 460), or SERVICE (coverage beyond assurance — separate
                performance obligation per 55-34). When any POB is SERVICE, reviewer must
                affirm before approval; actual sub-POB carve-out executes via Phase 8f bundling.
              </div>
              {warrantyLoading && (
                <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>Running warranty classification…</div>
              )}
              {!warrantyLoading && warranty && warranty.pool_size === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  No POBs to classify — warranty analysis will apply once POBs are materialized.
                </div>
              )}
              {!warrantyLoading && warranty && warranty.pool_size > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                    <span>NONE: {warranty.buckets.NONE}</span>
                    <span>ASSURANCE: {warranty.buckets.ASSURANCE}</span>
                    <span style={{ color: warranty.buckets.SERVICE > 0 ? 'var(--warning)' : undefined, fontWeight: warranty.buckets.SERVICE > 0 ? 600 : 400 }}>SERVICE: {warranty.buckets.SERVICE}</span>
                  </div>
                  {warranty.pobs.filter(p => p.warranty_type !== 'NONE').map((p) => (
                    <div key={p.pob_id} style={{
                      padding: 8,
                      border: `1px solid ${p.warranty_type === 'SERVICE' ? 'var(--warning)' : 'var(--border-subtle)'}`,
                      borderRadius: 4,
                      background: p.warranty_type === 'SERVICE' ? 'rgba(234,179,8,0.08)' : 'var(--bg-card)',
                      fontSize: 12,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600 }}>POB #{p.po_number}</span>
                          {p.obligation_name && <span style={{ marginLeft: 6, color: 'var(--text-secondary)' }}>· {p.obligation_name}</span>}
                          {p.description && <div style={{ marginTop: 2, color: 'var(--text-secondary)', fontSize: 11 }}>{p.description}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'flex-start' }}>
                          <span style={{
                            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                            background: p.warranty_type === 'SERVICE' ? 'var(--warning)' : 'rgba(59,130,246,0.15)',
                            color: p.warranty_type === 'SERVICE' ? 'white' : 'var(--accent)',
                          }}>{p.warranty_type}</span>
                          {p.warranty_term_months != null && (
                            <span style={{
                              padding: '1px 6px', borderRadius: 3, fontSize: 10,
                              background: 'var(--bg-surface)', color: 'var(--text-secondary)',
                              border: '1px solid var(--border-subtle)',
                            }}>{p.warranty_term_months}mo</span>
                          )}
                        </div>
                      </div>
                      {p.rationale && (
                        <div style={{ marginTop: 4, fontSize: 10, color: 'var(--text-secondary)' }}>
                          {p.rationale}
                        </div>
                      )}
                      {mode !== 'VIEW' && selected.review_status === 'PENDING' && (
                        <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => setWarrantyPanelPob({
                              id: p.pob_id,
                              label: `POB #${p.po_number}${p.obligation_name ? ' ' + p.obligation_name : ''}`,
                              // recognitionMethod is not part of the warranty classifier payload —
                              // the panel will fetch accrual-status which includes it from the POB row.
                              recognitionMethod: null,
                            })}
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              background: 'var(--accent)', color: 'white',
                              border: 'none', borderRadius: 3, cursor: 'pointer',
                            }}
                          >Assess Warranty</button>
                        </div>
                      )}
                    </div>
                  ))}
                  {mode !== 'VIEW' && selected.review_status === 'PENDING' && (
                    <div style={{ marginTop: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={step2WarrantyAffirmed}
                          onChange={(e) => setStep2WarrantyAffirmed(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <strong>I affirm the warranty classification.</strong>{' '}
                          {step2HasServiceWarranty
                            ? 'SERVICE-type warranty POBs will be carved out as separate ratable sub-POBs via Phase 8f bundling.'
                            : 'No service-type warranties detected; all warranty coverage is assurance-only (ASC 460 cost accrual at delivery).'}
                        </span>
                      </label>
                      <textarea
                        value={step2WarrantyNotes}
                        onChange={(e) => setStep2WarrantyNotes(e.target.value)}
                        placeholder="Optional: term negotiation, accrual rate override, or carve-out plan…"
                        style={{
                          width: '100%', marginTop: 6, padding: 6, fontSize: 11,
                          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                          borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 36,
                          fontFamily: 'inherit',
                        }}
                      />
                      {step2HasServiceWarranty && !step2WarrantyAffirmed && (
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--negative)' }}>
                          Approval blocked until reviewer affirms the warranty classification.
                        </div>
                      )}
                    </div>
                  )}
                  {mode === 'VIEW' && selected.step2_warranty_affirmed !== null && (
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                      Affirmed: {selected.step2_warranty_affirmed ? 'Yes' : 'No'}
                      {selected.step2_warranty_notes && ` · ${selected.step2_warranty_notes}`}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ASC 606 Phase 8d — Step 2 material right section */}
            <div style={{
              padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 6,
              background: step2MrRequiresSsp ? 'rgba(239,68,68,0.05)' : 'var(--bg-surface)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                ASC 606 Step 2 — Material Right / Customer Option (606-10-55-41 to 55-45)
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Customer options that provide below-standalone pricing are separate POBs.
                RENEWAL_OPTION uses the 55-45 practical alternative (expected consideration ×
                exercise probability). FUTURE_PURCHASE_DISCOUNT, LOYALTY, and OTHER types
                require the Phase 10 SSP library; reviewer must acknowledge the dependency.
              </div>
              {materialRightLoading && (
                <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>Running material right classification…</div>
              )}
              {!materialRightLoading && materialRight && materialRight.pool_size === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  No POBs to analyze for material rights.
                </div>
              )}
              {!materialRightLoading && materialRight && materialRight.pool_size > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                    <span>RENEWAL: {materialRight.type_counts.RENEWAL_OPTION}</span>
                    <span style={{ color: materialRight.type_counts.FUTURE_PURCHASE_DISCOUNT > 0 ? 'var(--negative)' : undefined }}>
                      FUT. DISCOUNT: {materialRight.type_counts.FUTURE_PURCHASE_DISCOUNT}
                    </span>
                    <span style={{ color: materialRight.type_counts.LOYALTY > 0 ? 'var(--negative)' : undefined }}>
                      LOYALTY: {materialRight.type_counts.LOYALTY}
                    </span>
                    <span style={{ color: materialRight.type_counts.OTHER > 0 ? 'var(--negative)' : undefined }}>
                      OTHER: {materialRight.type_counts.OTHER}
                    </span>
                  </div>
                  {materialRight.pobs.filter(p => p.is_material_right).map((p) => {
                    const needsSsp = p.material_right_type && p.material_right_type !== 'RENEWAL_OPTION';
                    return (
                      <div key={p.pob_id} style={{
                        padding: 8,
                        border: `1px solid ${needsSsp ? 'var(--negative)' : 'var(--accent)'}`,
                        borderRadius: 4,
                        background: needsSsp ? 'rgba(239,68,68,0.08)' : 'rgba(59,130,246,0.05)',
                        fontSize: 12,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: 600 }}>POB #{p.po_number}</span>
                            {p.obligation_name && <span style={{ marginLeft: 6, color: 'var(--text-secondary)' }}>· {p.obligation_name}</span>}
                            {p.description && <div style={{ marginTop: 2, color: 'var(--text-secondary)', fontSize: 11 }}>{p.description}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <span style={{
                              padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                              background: needsSsp ? 'var(--negative)' : 'var(--accent)',
                              color: 'white',
                            }}>{p.material_right_type}</span>
                            {needsSsp && (
                              <span style={{
                                padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                                background: 'var(--bg-surface)', color: 'var(--negative)',
                                border: '1px solid var(--negative)',
                              }}>needs SSP (P10)</span>
                            )}
                          </div>
                        </div>
                        {p.material_right_type === 'RENEWAL_OPTION' && (p.expected_consideration != null || p.exercise_probability != null) && (
                          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-secondary)' }}>
                            55-45 practical alternative: expected {p.expected_consideration != null ? formatAmount(p.expected_consideration) : '—'}
                            {' × probability '}{p.exercise_probability != null ? `${(p.exercise_probability * 100).toFixed(1)}%` : '—'}
                          </div>
                        )}
                        {p.rationale && (
                          <div style={{ marginTop: 4, fontSize: 10, color: 'var(--text-secondary)' }}>{p.rationale}</div>
                        )}
                      </div>
                    );
                  })}
                  {mode !== 'VIEW' && selected.review_status === 'PENDING' && (
                    <div style={{ marginTop: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={step2MaterialRightAffirmed}
                          onChange={(e) => setStep2MaterialRightAffirmed(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <strong>I affirm the material right classification.</strong>{' '}
                          {step2MrRequiresSsp
                            ? 'SSP-required material right(s) flagged — approval is conditional on Phase 10 SSP library; allocation will be reworked when Phase 10 ships.'
                            : materialRight.has_material_right
                              ? 'All material rights use the ASC 606-10-55-45 practical alternative — no Phase 10 dependency.'
                              : 'No material rights detected.'}
                        </span>
                      </label>
                      <textarea
                        value={step2MaterialRightNotes}
                        onChange={(e) => setStep2MaterialRightNotes(e.target.value)}
                        placeholder="Optional: override rationale or Phase 10 dependency plan…"
                        style={{
                          width: '100%', marginTop: 6, padding: 6, fontSize: 11,
                          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                          borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 36,
                          fontFamily: 'inherit',
                        }}
                      />
                      {step2MrRequiresSsp && !step2MaterialRightAffirmed && (
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--negative)' }}>
                          Approval blocked — affirm the Phase 10 SSP library dependency first.
                        </div>
                      )}
                    </div>
                  )}
                  {mode === 'VIEW' && selected.step2_material_right_affirmed !== null && (
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                      Affirmed: {selected.step2_material_right_affirmed ? 'Yes' : 'No'}
                      {selected.step2_material_right_notes && ` · ${selected.step2_material_right_notes}`}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ASC 606 Phase 8e — Step 2 principal-vs-agent section */}
            <div style={{
              padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 6,
              background: step2PaNeedsReview ? 'rgba(234,179,8,0.05)' : 'var(--bg-surface)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>ASC 606 Step 2 — Principal vs Agent (606-10-55-36 to 55-40)</span>
                {principalAgent && principalAgent.pool_size > 0 && (
                  <span style={{
                    padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                    background: principalAgent.predominant_role === 'ALL_PRINCIPAL' ? 'rgba(34,197,94,0.15)'
                      : principalAgent.predominant_role === 'ALL_AGENT' ? 'rgba(59,130,246,0.15)'
                      : 'rgba(234,179,8,0.15)',
                    color: principalAgent.predominant_role === 'ALL_PRINCIPAL' ? 'var(--positive)'
                      : principalAgent.predominant_role === 'ALL_AGENT' ? 'var(--accent)'
                      : 'var(--warning)',
                  }}>{principalAgent.predominant_role}</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                PRINCIPAL controls the good/service before transfer → GROSS revenue. AGENT
                arranges for another party → NET (commission only). The three 55-39 indicators
                (primary responsibility / inventory risk / price discretion) support the control
                assessment; no single indicator is dispositive (ASU 2016-08).
              </div>
              {principalAgentLoading && (
                <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>Running principal/agent classification…</div>
              )}
              {!principalAgentLoading && principalAgent && principalAgent.pool_size === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  No POBs to analyze for principal/agent determination.
                </div>
              )}
              {!principalAgentLoading && principalAgent && principalAgent.pool_size > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                    <span>PRINCIPAL: {principalAgent.counts.PRINCIPAL}</span>
                    <span style={{ color: principalAgent.counts.AGENT > 0 ? 'var(--accent)' : undefined, fontWeight: principalAgent.counts.AGENT > 0 ? 600 : 400 }}>
                      AGENT: {principalAgent.counts.AGENT}
                    </span>
                    <span style={{ color: principalAgent.counts.UNDETERMINED > 0 ? 'var(--warning)' : undefined, fontWeight: principalAgent.counts.UNDETERMINED > 0 ? 600 : 400 }}>
                      UNDETERMINED: {principalAgent.counts.UNDETERMINED}
                    </span>
                  </div>
                  {principalAgent.pobs.filter(p => p.entity_role !== 'PRINCIPAL').map((p) => (
                    <div key={p.pob_id} style={{
                      padding: 8,
                      border: `1px solid ${p.entity_role === 'UNDETERMINED' ? 'var(--warning)' : 'var(--accent)'}`,
                      borderRadius: 4,
                      background: p.entity_role === 'UNDETERMINED' ? 'rgba(234,179,8,0.08)' : 'rgba(59,130,246,0.05)',
                      fontSize: 12,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600 }}>POB #{p.po_number}</span>
                          {p.obligation_name && <span style={{ marginLeft: 6, color: 'var(--text-secondary)' }}>· {p.obligation_name}</span>}
                          {p.description && <div style={{ marginTop: 2, color: 'var(--text-secondary)', fontSize: 11 }}>{p.description}</div>}
                        </div>
                        <span style={{
                          padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                          background: p.entity_role === 'UNDETERMINED' ? 'var(--warning)' : 'var(--accent)',
                          color: 'white', flexShrink: 0,
                        }}>{p.entity_role}</span>
                      </div>
                      {p.indicators && (
                        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2, fontSize: 10 }}>
                          {(['primary_responsibility', 'inventory_risk', 'price_discretion'] as const).map(k => (
                            <div key={k} style={{ color: 'var(--text-secondary)' }}>
                              <span style={{ color: p.indicators![k].value ? 'var(--positive)' : 'var(--negative)', fontWeight: 600 }}>
                                {p.indicators![k].value ? '\u2713' : '\u2717'}
                              </span>{' '}
                              {k.replace(/_/g, ' ')}
                              {p.indicators![k].evidence && ` — ${p.indicators![k].evidence}`}
                            </div>
                          ))}
                        </div>
                      )}
                      {p.rationale && (
                        <div style={{ marginTop: 4, fontSize: 10, color: 'var(--text-secondary)' }}>{p.rationale}</div>
                      )}
                      {mode !== 'VIEW' && selected.review_status === 'PENDING' && (
                        <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setPaPanelPob({
                              id: p.pob_id,
                              label: `POB #${p.po_number}${p.obligation_name ? ' ' + p.obligation_name : ''}`,
                            })}
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              background: 'var(--accent)', color: 'white',
                              border: 'none', borderRadius: 3, cursor: 'pointer',
                            }}
                          >Classify P/A</button>
                        </div>
                      )}
                    </div>
                  ))}
                  {mode !== 'VIEW' && selected.review_status === 'PENDING' && (
                    <div style={{ marginTop: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={step2PaAffirmed}
                          onChange={(e) => setStep2PaAffirmed(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <strong>I affirm the principal/agent classification.</strong>{' '}
                          {principalAgent.has_undetermined
                            ? 'UNDETERMINED POBs require reviewer judgment — confirm the control indicators via 8f bundling / direct POB edit.'
                            : principalAgent.has_agent
                              ? 'AGENT POBs will be recognized NET; commission revenue + supplier pass-through liability per 55-38.'
                              : 'All POBs classified PRINCIPAL — revenue recognized GROSS per existing flow.'}
                        </span>
                      </label>
                      <textarea
                        value={step2PaNotes}
                        onChange={(e) => setStep2PaNotes(e.target.value)}
                        placeholder="Optional: indicator overrides, evidence narrative, MIXED contract rationale…"
                        style={{
                          width: '100%', marginTop: 6, padding: 6, fontSize: 11,
                          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                          borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 36,
                          fontFamily: 'inherit',
                        }}
                      />
                      {step2PaNeedsReview && !step2PaAffirmed && (
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--negative)' }}>
                          Approval blocked — affirm the principal/agent analysis first.
                        </div>
                      )}
                    </div>
                  )}
                  {mode === 'VIEW' && selected.step2_principal_agent_affirmed !== null && (
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                      Affirmed: {selected.step2_principal_agent_affirmed ? 'Yes' : 'No'}
                      {selected.step2_principal_agent_notes && ` · ${selected.step2_principal_agent_notes}`}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ASC 606 Phase 9a — Step 3 Variable Consideration section */}
            <div style={{
              padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 6,
              background: 'var(--bg-surface)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Step 3 — Variable Consideration (ASC 606-10-32-5 to 32-13)</span>
                {variableConsideration && variableConsideration.has_components && (
                  <span style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 10,
                    background: step3HasUnquantified ? 'rgba(239,68,68,0.15)'
                      : variableConsideration.components.some(c => c.constraint_applied)
                        ? 'rgba(234,179,8,0.15)'
                        : 'rgba(34,197,94,0.15)',
                    color: step3HasUnquantified ? 'var(--negative)'
                      : variableConsideration.components.some(c => c.constraint_applied)
                        ? 'var(--warning)' : 'var(--positive)',
                    fontWeight: 600,
                  }}>
                    {variableConsideration.components.length} component{variableConsideration.components.length === 1 ? '' : 's'}
                    {step3HasUnquantified && ' — needs estimate'}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Rebates, bonuses, penalties, refunds, discounts, and price escalations.
                32-11 constraint gates upside inclusion by probability; downside is always
                included. Threshold read from Client Policies (CONSTRAINT_PROBABILITY_THRESHOLD).
              </div>
              {variableConsiderationError && (
                <div style={{
                  padding: 8, marginBottom: 8, fontSize: 11,
                  background: 'rgba(239,68,68,0.10)', color: 'var(--negative)',
                  border: '1px solid rgba(239,68,68,0.30)', borderRadius: 4,
                }}>{variableConsiderationError}</div>
              )}
              {variableConsiderationLoading && (
                <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>Loading variable consideration…</div>
              )}
              {!variableConsiderationLoading && variableConsideration && (
                <div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
                    <span>Aggregate: <strong style={{ color: variableConsideration.variable_consideration_aggregate >= 0 ? 'var(--positive)' : 'var(--negative)' }}>
                      ${variableConsideration.variable_consideration_aggregate.toLocaleString()}
                    </strong></span>
                    <span>Constraint threshold: {
                      variableConsideration.constraint_probability_threshold !== null
                        ? `${(variableConsideration.constraint_probability_threshold * 100).toFixed(0)}%`
                        : <span style={{ color: 'var(--warning)' }}>not configured</span>
                    }</span>
                  </div>
                  {variableConsideration.components.length === 0 && (
                    <div style={{ fontSize: 11, color: 'var(--text-hint)', fontStyle: 'italic' }}>
                      No variable consideration components detected — contract has a fixed transaction price.
                    </div>
                  )}
                  {variableConsideration.components.map((c) => (
                    <div key={c.id} style={{
                      padding: 8, marginBottom: 6, fontSize: 11,
                      background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                      borderRadius: 4,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600 }}>
                          {c.component_type}
                          {c.constraint_applied && (
                            <span style={{
                              marginLeft: 8, fontSize: 10, padding: '1px 6px',
                              borderRadius: 8, background: 'rgba(234,179,8,0.20)',
                              color: 'var(--warning)',
                            }}>32-11 constrained</span>
                          )}
                        </span>
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {c.estimation_method === 'EXPECTED_VALUE' ? 'Expected value' : 'Most likely'}
                          {c.probability !== null && ` · p=${(c.probability * 100).toFixed(0)}%`}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, color: 'var(--text-secondary)' }}>
                        <span>Est: <strong>${c.estimated_amount.toLocaleString()}</strong></span>
                        <span>Included: <strong style={{ color: c.constrained_amount === 0 ? 'var(--text-hint)' : 'var(--text-primary)' }}>
                          ${c.constrained_amount.toLocaleString()}
                        </strong></span>
                        {c.pending_adjustment_amount !== 0 && (
                          <span style={{ color: 'var(--warning)' }}>
                            Pending Δ: ${c.pending_adjustment_amount.toLocaleString()}
                          </span>
                        )}
                      </div>
                      {c.rationale && (
                        <div style={{ marginTop: 4, color: 'var(--text-hint)', fontStyle: 'italic' }}>
                          {c.rationale}
                        </div>
                      )}
                    </div>
                  ))}
                  {mode !== 'VIEW' && selected.review_status === 'PENDING' && variableConsideration.has_components && (
                    <div style={{ marginTop: 8 }}>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer', fontSize: 11 }}>
                        <input
                          type="checkbox"
                          checked={step3VcAffirmed}
                          onChange={(e) => setStep3VcAffirmed(e.target.checked)}
                          disabled={step3HasUnquantified}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <strong>I affirm variable consideration review.</strong>{' '}
                          {step3HasUnquantified
                            ? 'Unquantified component(s) present — supply estimate and probability before affirming.'
                            : 'Estimates, probabilities, and 32-11 constraint decisions reviewed.'}
                        </span>
                      </label>
                      <textarea
                        value={step3VcNotes}
                        onChange={(e) => setStep3VcNotes(e.target.value)}
                        placeholder="Optional: estimate basis, probability rationale, constraint decisions…"
                        style={{
                          width: '100%', marginTop: 6, padding: 6, fontSize: 11,
                          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                          borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 36,
                          fontFamily: 'inherit',
                        }}
                      />
                      {!step3VcGateOk && (
                        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--negative)' }}>
                          Approval blocked — {step3HasUnquantified
                            ? 'quantify component(s) first.'
                            : 'affirm the Step 3 variable consideration analysis.'}
                        </div>
                      )}
                    </div>
                  )}
                  {mode === 'VIEW' && selected.step3_variable_consideration_affirmed !== null && (
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                      Affirmed: {selected.step3_variable_consideration_affirmed ? 'Yes' : 'No'}
                      {selected.step3_variable_consideration_notes && ` · ${selected.step3_variable_consideration_notes}`}
                    </div>
                  )}
                  {/* Phase 9c — Financing subsection */}
                  <div style={{
                    marginTop: 10, padding: 8, fontSize: 11,
                    background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                      <span>Significant Financing Component (ASC 606-10-32-15 to 32-20)</span>
                      {financing && (
                        <span style={{
                          fontSize: 10, padding: '1px 6px', borderRadius: 8,
                          background: financing.applies_practical_expedient ? 'rgba(148,163,184,0.20)'
                            : financing.significant ? 'rgba(234,179,8,0.20)'
                            : 'rgba(34,197,94,0.15)',
                          color: financing.applies_practical_expedient ? 'var(--text-secondary)'
                            : financing.significant ? 'var(--warning)' : 'var(--positive)',
                          fontWeight: 600,
                        }}>
                          {financing.applies_practical_expedient ? '32-18 expedient (≤12m)'
                            : financing.significant ? 'SIGNIFICANT' : 'insignificant'}
                        </span>
                      )}
                    </div>
                    {financingError && (
                      <div style={{
                        padding: 6, marginBottom: 6, fontSize: 11,
                        background: 'rgba(239,68,68,0.10)', color: 'var(--negative)',
                        border: '1px solid rgba(239,68,68,0.30)', borderRadius: 4,
                      }}>{financingError}</div>
                    )}
                    {financingLoading && <div style={{ color: 'var(--text-hint)' }}>Loading…</div>}
                    {!financingLoading && financing && (
                      <>
                        <div style={{ display: 'flex', gap: 12, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                          <span>Duration: {financing.duration_months !== null ? `${financing.duration_months}m` : 'n/a'}</span>
                          <span>Rate: {financing.effective_rate !== null ? `${(financing.effective_rate * 100).toFixed(2)}%` : 'n/a'}</span>
                          <span>Source: {financing.rate_source}</span>
                          <span>Face: ${financing.face_value.toLocaleString()}</span>
                          <span>PV: ${financing.present_value.toLocaleString()}</span>
                          {financing.adjustment !== 0 && (
                            <span>Adj: <strong style={{ color: financing.adjustment > 0 ? 'var(--positive)' : 'var(--negative)' }}>
                              ${financing.adjustment.toLocaleString()}
                            </strong></span>
                          )}
                        </div>
                        {mode !== 'VIEW' && selected.review_status === 'PENDING' && financing.significant && (
                          <div style={{ marginTop: 6 }}>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={step3FinancingAffirmed}
                                onChange={(e) => setStep3FinancingAffirmed(e.target.checked)}
                                style={{ marginTop: 2 }}
                              />
                              <span>I affirm the imputed rate + PV adjustment for significant financing.</span>
                            </label>
                            <textarea
                              value={step3FinancingNotes}
                              onChange={(e) => setStep3FinancingNotes(e.target.value)}
                              placeholder="Optional: rate justification, PV calculation notes…"
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 30,
                                fontFamily: 'inherit',
                              }}
                            />
                            {!step3FinancingGateOk && (
                              <div style={{ marginTop: 4, color: 'var(--negative)' }}>
                                Approval blocked — affirm the financing analysis.
                              </div>
                            )}
                          </div>
                        )}
                        {mode === 'VIEW' && selected.step3_financing_affirmed !== null && (
                          <div style={{ marginTop: 4, color: 'var(--text-secondary)' }}>
                            Affirmed: {selected.step3_financing_affirmed ? 'Yes' : 'No'}
                            {selected.step3_financing_notes && ` · ${selected.step3_financing_notes}`}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Phase 9c — Noncash subsection */}
                  <div style={{
                    marginTop: 10, padding: 8, fontSize: 11,
                    background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                      <span>Noncash Consideration (ASC 606-10-32-21 to 32-24)</span>
                      {noncash && (
                        <span style={{
                          fontSize: 10, padding: '1px 6px', borderRadius: 8,
                          background: !noncash.detected ? 'rgba(148,163,184,0.20)'
                            : noncash.fv_recorded ? 'rgba(34,197,94,0.15)'
                            : 'rgba(239,68,68,0.15)',
                          color: !noncash.detected ? 'var(--text-secondary)'
                            : noncash.fv_recorded ? 'var(--positive)' : 'var(--negative)',
                          fontWeight: 600,
                        }}>
                          {!noncash.detected ? 'none detected'
                            : noncash.fv_recorded ? 'FV recorded' : 'FV required'}
                        </span>
                      )}
                    </div>
                    {noncashLoading && <div style={{ color: 'var(--text-hint)' }}>Loading…</div>}
                    {!noncashLoading && noncash && (
                      <>
                        {noncash.detected && (
                          <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>
                            Matched: <strong>{noncash.matched_keyword}</strong>
                          </div>
                        )}
                        <div style={{ color: 'var(--text-hint)', fontStyle: 'italic', marginBottom: 6 }}>
                          {noncash.rationale}
                        </div>
                        {mode !== 'VIEW' && selected.review_status === 'PENDING' && noncash.detected && (
                          <div>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={noncashFvInput}
                                onChange={(e) => setNoncashFvInput(e.target.value)}
                                placeholder="Inception FV ($)"
                                style={{
                                  flex: 1, padding: 4, fontSize: 11,
                                  background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                                  borderRadius: 4, color: 'var(--text-primary)',
                                }}
                              />
                              <input
                                type="text"
                                value={noncashDescInput}
                                onChange={(e) => setNoncashDescInput(e.target.value)}
                                placeholder="Description (shares, FMV source, etc.)"
                                style={{
                                  flex: 2, padding: 4, fontSize: 11,
                                  background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                                  borderRadius: 4, color: 'var(--text-primary)',
                                }}
                              />
                            </div>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={step3NoncashAffirmed}
                                onChange={(e) => setStep3NoncashAffirmed(e.target.checked)}
                                disabled={!step3NoncashFvRecorded}
                                style={{ marginTop: 2 }}
                              />
                              <span>I affirm the inception fair value measurement per ASC 606-10-32-22.</span>
                            </label>
                            <textarea
                              value={step3NoncashNotes}
                              onChange={(e) => setStep3NoncashNotes(e.target.value)}
                              placeholder="Optional: FV source, appraiser, SSP reference…"
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 30,
                                fontFamily: 'inherit',
                              }}
                            />
                            {!step3NoncashGateOk && (
                              <div style={{ marginTop: 4, color: 'var(--negative)' }}>
                                Approval blocked — {step3NoncashDetected && !step3NoncashFvRecorded
                                  ? 'supply FV + description.'
                                  : 'affirm the noncash analysis.'}
                              </div>
                            )}
                          </div>
                        )}
                        {mode === 'VIEW' && selected.step3_noncash_affirmed !== null && (
                          <div style={{ marginTop: 4, color: 'var(--text-secondary)' }}>
                            FV: {selected.noncash_consideration_fv !== null && selected.noncash_consideration_fv !== undefined
                              ? `$${selected.noncash_consideration_fv.toLocaleString()}` : 'n/a'}
                            {selected.noncash_description && ` · ${selected.noncash_description}`}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Phase 9e — Consideration Payable to Customer */}
                  <div style={{
                    marginTop: 10, padding: 8, fontSize: 11,
                    background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                      <span>Consideration Payable to Customer (ASC 606-10-32-25 to 32-27)</span>
                      {cpsRows.length > 0 && (
                        <span style={{
                          fontSize: 10, padding: '1px 6px', borderRadius: 8,
                          background: step3CpsHasActive ? 'rgba(234,179,8,0.15)' : 'rgba(148,163,184,0.20)',
                          color: step3CpsHasActive ? 'var(--warning)' : 'var(--text-secondary)',
                          fontWeight: 600,
                        }}>{cpsRows.length} row{cpsRows.length === 1 ? '' : 's'}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                      Slotting fees, coop advertising, rebates, promotional payments.
                      Branch A (distinct service) → expense at FV + TP reduction for excess.
                      Branch B (no distinct service) → TP reduction for full amount.
                    </div>
                    {cpsRows.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        {cpsRows.map(p => (
                          <div key={p.id} style={{
                            padding: 6, marginBottom: 4,
                            background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                            borderRadius: 3,
                          }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{
                                fontSize: 10, padding: '1px 6px', borderRadius: 8, fontWeight: 600,
                                background: p.status === 'OPEN' ? 'rgba(234,179,8,0.20)'
                                  : p.status === 'ACCRUED' ? 'rgba(59,130,246,0.20)'
                                  : p.status === 'SETTLED' ? 'rgba(34,197,94,0.15)'
                                  : 'rgba(148,163,184,0.20)',
                                color: p.status === 'OPEN' ? 'var(--warning)'
                                  : p.status === 'ACCRUED' ? 'var(--accent)'
                                  : p.status === 'SETTLED' ? 'var(--positive)'
                                  : 'var(--text-secondary)',
                              }}>{p.status}</span>
                              <span style={{ fontWeight: 600 }}>{p.payment_type}</span>
                              <span style={{ color: 'var(--text-secondary)' }}>
                                {p.classification === 'DISTINCT_SERVICE_RECEIVED' ? '32-26(a)' : '32-26(b)'}
                              </span>
                              <span>gross=${p.gross_amount.toLocaleString()}</span>
                              {p.expense_amount > 0 && <span>exp=${p.expense_amount.toLocaleString()}</span>}
                              {p.tp_reduction_amount > 0 && <span>TP-red=${p.tp_reduction_amount.toLocaleString()}</span>}
                              {p.balance_due > 0 && (
                                <span style={{ color: 'var(--warning)' }}>bal=${p.balance_due.toLocaleString()}</span>
                              )}
                            </div>
                            <div style={{ color: 'var(--text-hint)', fontStyle: 'italic', marginTop: 2 }}>{p.description}</div>
                            {mode !== 'VIEW' && (
                              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                {p.status === 'OPEN' && (
                                  <button
                                    type="button"
                                    onClick={() => cpsAction(p.id, 'accrue')}
                                    style={{
                                      padding: '2px 8px', fontSize: 10,
                                      background: 'var(--accent)', color: 'white',
                                      border: 'none', borderRadius: 3, cursor: 'pointer',
                                    }}
                                  >Accrue</button>
                                )}
                                {p.status === 'ACCRUED' && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const amt = prompt(`Settle amount (balance=$${p.balance_due}):`);
                                      if (amt) cpsAction(p.id, 'settle', { amount: parseFloat(amt) });
                                    }}
                                    style={{
                                      padding: '2px 8px', fontSize: 10,
                                      background: 'var(--positive)', color: 'white',
                                      border: 'none', borderRadius: 3, cursor: 'pointer',
                                    }}
                                  >Settle</button>
                                )}
                                {(p.status === 'OPEN' || p.status === 'ACCRUED') && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const r = prompt('Release reason:');
                                      if (r) cpsAction(p.id, 'release', { reason: r });
                                    }}
                                    style={{
                                      padding: '2px 8px', fontSize: 10,
                                      background: 'transparent', color: 'var(--text-secondary)',
                                      border: '1px solid var(--border-subtle)', borderRadius: 3, cursor: 'pointer',
                                    }}
                                  >Release</button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {mode !== 'VIEW' && (
                      <div style={{ padding: 6, background: 'var(--bg-surface)', borderRadius: 4, marginBottom: 6 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Add CPS row</div>
                        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                          <select
                            value={cpsForm.payment_type}
                            onChange={(e) => setCpsForm({ ...cpsForm, payment_type: e.target.value })}
                            aria-label="CPS payment type"
                            style={{
                              flex: 1, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)',
                            }}
                          >
                            <option value="SLOTTING_FEE">SLOTTING_FEE</option>
                            <option value="COOP_ADVERTISING">COOP_ADVERTISING</option>
                            <option value="REBATE">REBATE</option>
                            <option value="PROMOTIONAL">PROMOTIONAL</option>
                            <option value="OTHER">OTHER</option>
                          </select>
                          <select
                            value={cpsForm.classification}
                            onChange={(e) => setCpsForm({ ...cpsForm, classification: e.target.value })}
                            aria-label="CPS classification branch"
                            style={{
                              flex: 2, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)',
                            }}
                          >
                            <option value="TRANSACTION_PRICE_REDUCTION">TRANSACTION_PRICE_REDUCTION (32-26(b))</option>
                            <option value="DISTINCT_SERVICE_RECEIVED">DISTINCT_SERVICE_RECEIVED (32-26(a))</option>
                          </select>
                        </div>
                        <input
                          type="text"
                          value={cpsForm.description}
                          onChange={(e) => setCpsForm({ ...cpsForm, description: e.target.value })}
                          placeholder="Description"
                          style={{
                            width: '100%', marginBottom: 4, padding: 4, fontSize: 11,
                            background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                            borderRadius: 4, color: 'var(--text-primary)',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={cpsForm.gross_amount}
                            onChange={(e) => setCpsForm({ ...cpsForm, gross_amount: e.target.value })}
                            placeholder="Gross ($)"
                            style={{
                              flex: 1, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)',
                            }}
                          />
                          {cpsForm.classification === 'DISTINCT_SERVICE_RECEIVED' && (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={cpsForm.distinct_service_fair_value}
                              onChange={(e) => setCpsForm({ ...cpsForm, distinct_service_fair_value: e.target.value })}
                              placeholder="Fair value ($)"
                              style={{
                                flex: 1, padding: 4, fontSize: 11,
                                background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)',
                              }}
                            />
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={submitCps}
                            disabled={cpsSubmitting}
                            style={{
                              padding: '3px 10px', fontSize: 10, fontWeight: 600,
                              background: 'var(--accent)', color: 'white',
                              border: 'none', borderRadius: 3,
                              cursor: cpsSubmitting ? 'wait' : 'pointer',
                              opacity: cpsSubmitting ? 0.7 : 1,
                            }}
                          >
                            {cpsSubmitting ? 'Adding…' : 'Add CPS'}
                          </button>
                          {cpsError && (
                            <span style={{ color: 'var(--negative)', fontSize: 11 }}>{cpsError}</span>
                          )}
                        </div>
                      </div>
                    )}
                    {mode !== 'VIEW' && selected.review_status === 'PENDING' && step3CpsHasActive && (
                      <div>
                        <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                          <input
                            type="checkbox"
                            checked={step3CpsAffirmed}
                            onChange={(e) => setStep3CpsAffirmed(e.target.checked)}
                            style={{ marginTop: 2 }}
                          />
                          <span>I affirm CPS classification + amounts for all rows.</span>
                        </label>
                        <textarea
                          value={step3CpsNotes}
                          onChange={(e) => setStep3CpsNotes(e.target.value)}
                          placeholder="Optional: classification rationale, FV source, timing notes…"
                          style={{
                            width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                            background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                            borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 30,
                            fontFamily: 'inherit',
                          }}
                        />
                        {!step3CpsGateOk && (
                          <div style={{ marginTop: 4, color: 'var(--negative)', fontSize: 11 }}>
                            Approval blocked — affirm the CPS analysis.
                          </div>
                        )}
                      </div>
                    )}
                    {mode === 'VIEW' && selected.step3_cps_affirmed !== null && (
                      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-secondary)' }}>
                        Affirmed: {selected.step3_cps_affirmed ? 'Yes' : 'No'}
                        {selected.step3_cps_notes && ` · ${selected.step3_cps_notes}`}
                      </div>
                    )}
                  </div>

                  {/* ASC 606 Phase 10e — Step 4 Allocation (32-28 through 32-41) */}
                  <div style={{
                    marginTop: 10, padding: 8, fontSize: 11,
                    background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                      Step 4 · Allocate Transaction Price (ASC 606-10-32-28 to 32-41)
                    </div>
                    <AllocationPreviewPanel contractId={selected.revenue_contract_id} />

                    {mode !== 'VIEW' && selected.review_status === 'PENDING' && (
                      <>
                        {/* Always-required allocation affirmation */}
                        <div style={{ marginTop: 8 }}>
                          <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                            <input
                              type="checkbox"
                              checked={step4AllocAffirmed}
                              onChange={(e) => setStep4AllocAffirmed(e.target.checked)}
                              style={{ marginTop: 2 }}
                            />
                            <span>
                              I affirm the Step 4 allocation — per-POB allocated_price matches
                              the contract's economic reality per the 32-28 allocation objective.
                              <strong> Required for every approval.</strong>
                            </span>
                          </label>
                          <textarea
                            value={step4AllocNotes}
                            onChange={(e) => setStep4AllocNotes(e.target.value)}
                            placeholder="Optional: allocation rationale, reviewer notes…"
                            style={{
                              width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                              background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 30,
                              fontFamily: 'inherit',
                            }}
                          />
                        </div>

                        {/* 32-37 specific discount — conditional */}
                        <div style={{
                          marginTop: 10, padding: 6,
                          background: 'var(--bg-surface)', borderRadius: 4,
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            32-37 Specific Discount Allocation
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                            Required only if any POB has a specific discount set (Phase 10c).
                            Affirm all three criteria when used.
                          </div>
                          <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                            <input type="checkbox" checked={step4SdAffirmed}
                              onChange={(e) => setStep4SdAffirmed(e.target.checked)}
                              style={{ marginTop: 2 }} />
                            <span>Specific discount allocation affirmed</span>
                          </label>
                          <div style={{ marginLeft: 20, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                              <input type="checkbox" checked={step4SdCrit1}
                                onChange={(e) => setStep4SdCrit1(e.target.checked)} />
                              <span>(a) Entity regularly sells each distinct G/S on a standalone basis</span>
                            </label>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                              <input type="checkbox" checked={step4SdCrit2}
                                onChange={(e) => setStep4SdCrit2(e.target.checked)} />
                              <span>(b) Entity regularly sells the bundle at a discount vs sum-of-SSPs</span>
                            </label>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                              <input type="checkbox" checked={step4SdCrit3}
                                onChange={(e) => setStep4SdCrit3(e.target.checked)} />
                              <span>(c) Discount substantially the same with observable evidence of target POB(s)</span>
                            </label>
                          </div>
                          <textarea
                            value={step4SdNotes}
                            onChange={(e) => setStep4SdNotes(e.target.value)}
                            placeholder="Observable-evidence narrative (pricing history, similar bundles, catalogs)…"
                            style={{
                              width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 30,
                              fontFamily: 'inherit',
                            }}
                          />
                        </div>

                        {/* 32-40 specific VC — conditional */}
                        <div style={{
                          marginTop: 8, padding: 6,
                          background: 'var(--bg-surface)', borderRadius: 4,
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            32-40 Variable Consideration — Specific Allocation
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                            Required only if any VC component is targeted to a specific POB
                            (allocated_to_pob_id set, Phase 10d). Affirm both criteria when used.
                          </div>
                          <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                            <input type="checkbox" checked={step4VcSpecAffirmed}
                              onChange={(e) => setStep4VcSpecAffirmed(e.target.checked)}
                              style={{ marginTop: 2 }} />
                            <span>VC specific allocation affirmed</span>
                          </label>
                          <div style={{ marginLeft: 20, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                              <input type="checkbox" checked={step4VcCrit1}
                                onChange={(e) => setStep4VcCrit1(e.target.checked)} />
                              <span>(a) Variable payment terms relate specifically to the target POB</span>
                            </label>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                              <input type="checkbox" checked={step4VcCrit2}
                                onChange={(e) => setStep4VcCrit2(e.target.checked)} />
                              <span>(b) Allocating entirely to the POB faithfully depicts expected consideration per 32-28</span>
                            </label>
                          </div>
                          <textarea
                            value={step4VcSpecNotes}
                            onChange={(e) => setStep4VcSpecNotes(e.target.value)}
                            placeholder="Why the VC terms attach specifically to the named POB…"
                            style={{
                              width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 30,
                              fontFamily: 'inherit',
                            }}
                          />
                        </div>

                        {/* 32-34(c) residual approach — conditional */}
                        <div style={{
                          marginTop: 8, padding: 6,
                          background: 'var(--bg-surface)', borderRadius: 4,
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            32-34(c) Residual Approach
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                            Required only if any POB has ssp_source=RESIDUAL. One of two
                            qualifying conditions must apply.
                          </div>
                          <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                            <input type="checkbox" checked={step4ResAttested}
                              onChange={(e) => setStep4ResAttested(e.target.checked)}
                              style={{ marginTop: 2 }} />
                            <span>Residual approach attested</span>
                          </label>
                          <div style={{ marginLeft: 20, marginBottom: 4 }}>
                            <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                              Condition:&nbsp;
                              <select
                                value={step4ResCondition}
                                onChange={(e) => setStep4ResCondition(e.target.value)}
                                style={{
                                  padding: 2, fontSize: 11,
                                  background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                  borderRadius: 4, color: 'var(--text-primary)',
                                }}
                              >
                                <option value="">(select)</option>
                                <option value="HIGHLY_VARIABLE">HIGHLY_VARIABLE (32-34(c)(1))</option>
                                <option value="SSP_NOT_ESTABLISHED">SSP_NOT_ESTABLISHED (32-34(c)(2))</option>
                              </select>
                            </label>
                          </div>
                          <textarea
                            value={step4ResNotes}
                            onChange={(e) => setStep4ResNotes(e.target.value)}
                            placeholder="Supporting evidence — price variance data, standalone-sale history…"
                            style={{
                              width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 30,
                              fontFamily: 'inherit',
                            }}
                          />
                        </div>
                      </>
                    )}

                    {mode === 'VIEW' && selected.step4_allocation_affirmed !== null && (
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                        Allocation Affirmed: {selected.step4_allocation_affirmed ? 'Yes' : 'No'}
                        {selected.step4_allocation_notes && ` · ${selected.step4_allocation_notes}`}
                        {selected.step4_specific_discount_affirmed !== null && (
                          <div>Discount (32-37): {selected.step4_specific_discount_affirmed ? 'Affirmed' : '—'}</div>
                        )}
                        {selected.step4_vc_specific_affirmed !== null && (
                          <div>VC Specific (32-40): {selected.step4_vc_specific_affirmed ? 'Affirmed' : '—'}</div>
                        )}
                        {selected.step4_residual_attested !== null && selected.step4_residual_condition && (
                          <div>Residual (32-34(c)): {selected.step4_residual_condition}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ASC 606 Phase 11f — Step 5 Recognition Attestations (25-27 → 25-37 + 55-18 + ASC 450) */}
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                      Step 5 — Recognize Revenue (ASC 606-10-25-27 → 25-37)
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
                      Reviewer attestations for over-time qualification, progress method, and
                      conditional method-specific gates.
                    </div>

                    {/* Pre-flight hard-block banners */}
                    {step5PreflightBlocks.length > 0 && (
                      <div style={{
                        padding: 10, marginBottom: 10,
                        background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)',
                        borderRadius: 4, fontSize: 12, color: 'var(--negative)',
                      }}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>
                          Step 5 hard block — approval will fail:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {step5PreflightBlocks.map((code) => (
                            <li key={code}>
                              <strong>{code}</strong>
                              {code === 'STEP5_PENDING_ESTIMATE_REVISION_UNRESOLVED' && (
                                <> — a POB has a staged 11e revision. Drain via daily consumer or cancel in Progress Tracker &gt; Estimate Revisions.</>
                              )}
                              {code === 'STEP5_UNSUPPORTED_PROGRESS_METHOD' && (
                                <> — a POB is set to LABOR_HOURS or OUTPUT_SURVEY. No recognition engine ships yet; re-classify to COST_TO_COST, MILESTONES_COMPLETED, UNITS_DELIVERED, or TIME_ELAPSED.</>
                              )}
                              {code === 'STEP5_CLASSIFIER_NOT_RUN' && (
                                <> — a POB has progress_method set but over_time_qualified is NULL. Run the 25-27 classifier in Progress Tracker.</>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {!step5HasOverTimePob && step5PreflightBlocks.length === 0 && (
                      <div style={{
                        padding: 10, marginBottom: 10,
                        background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                        borderRadius: 4, fontSize: 12, color: 'var(--text-secondary)',
                      }}>
                        No POB has a progress_method set — Step 5 is trivially satisfied.
                        Attestations default to True on approval.
                      </div>
                    )}

                    {step5HasOverTimePob && step5PreflightBlocks.length === 0 && mode !== 'VIEW' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {/* Always-required: 25-27 qualification */}
                        <div style={{
                          padding: 10, border: '1px solid var(--border-subtle)',
                          borderRadius: 4, background: 'var(--bg-surface)',
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            Over-time qualification (25-27)
                          </div>
                          <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                            <input type="checkbox" checked={step5OtAffirmed}
                              onChange={(e) => setStep5OtAffirmed(e.target.checked)} />
                            <span>I affirm the POB(s) qualify for over-time recognition.</span>
                          </label>
                          <textarea
                            value={step5OtNotes}
                            onChange={(e) => setStep5OtNotes(e.target.value)}
                            placeholder="Qualification rationale — which 25-27 criterion applies and why..."
                            style={{
                              width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)',
                              resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                            }}
                          />
                          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>
                            Individual 25-27 criteria (separate SOX 404 booleans — set each Yes/No):
                          </div>
                          {([
                            ['25-27(a) Simultaneous consumption', step5OtCritA, setStep5OtCritA],
                            ['25-27(b) Customer controls the asset', step5OtCritB, setStep5OtCritB],
                            ['25-27(c) No alternative use + right to payment', step5OtCritC, setStep5OtCritC],
                          ] as const).map(([label, val, setter]) => (
                            <div key={label} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, marginTop: 4 }}>
                              <span style={{ minWidth: 260 }}>{label}</span>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  checked={val === true}
                                  onChange={() => setter(true)}
                                />
                                <span>Yes</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                <input
                                  type="radio"
                                  checked={val === false}
                                  onChange={() => setter(false)}
                                />
                                <span>No</span>
                              </label>
                            </div>
                          ))}
                        </div>

                        {/* Always-required: progress_method affirmed */}
                        <div style={{
                          padding: 10, border: '1px solid var(--border-subtle)',
                          borderRadius: 4, background: 'var(--bg-surface)',
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            Progress method (25-32 single-method)
                          </div>
                          <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                            <input type="checkbox" checked={step5PmAffirmed}
                              onChange={(e) => setStep5PmAffirmed(e.target.checked)} />
                            <span>I affirm the selected progress method faithfully depicts transfer of control.</span>
                          </label>
                          <textarea
                            value={step5PmNotes}
                            onChange={(e) => setStep5PmNotes(e.target.value)}
                            placeholder="Method selection rationale..."
                            style={{
                              width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)',
                              resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                            }}
                          />
                        </div>

                        {/* Conditional: COST_TO_COST */}
                        {step5HasCostToCost && (
                          <div style={{
                            padding: 10, border: '1px solid var(--border-subtle)',
                            borderRadius: 4, background: 'var(--bg-surface)',
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              COST_TO_COST gates (25-35 + 25-36)
                            </div>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                              <input type="checkbox" checked={step5CostBaselineAffirmed}
                                onChange={(e) => setStep5CostBaselineAffirmed(e.target.checked)} />
                              <span>I affirm the cost baseline (estimated_total_cost) is reasonable and audit trail on file.</span>
                            </label>
                            <textarea
                              value={step5CostBaselineNotes}
                              onChange={(e) => setStep5CostBaselineNotes(e.target.value)}
                              placeholder="Baseline rationale..."
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)',
                                resize: 'vertical', minHeight: 36, fontFamily: 'inherit',
                              }}
                            />
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginTop: 8 }}>
                              <input type="checkbox" checked={step5UninstalledAffirmed}
                                onChange={(e) => setStep5UninstalledAffirmed(e.target.checked)} />
                              <span>I affirm 25-36 uninstalled-materials carve-outs are correctly applied.</span>
                            </label>
                            <textarea
                              value={step5UninstalledNotes}
                              onChange={(e) => setStep5UninstalledNotes(e.target.value)}
                              placeholder="Carve-out rationale..."
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)',
                                resize: 'vertical', minHeight: 36, fontFamily: 'inherit',
                              }}
                            />
                          </div>
                        )}

                        {/* Conditional: MILESTONES_COMPLETED */}
                        {step5HasMilestones && (
                          <div style={{
                            padding: 10, border: '1px solid var(--border-subtle)',
                            borderRadius: 4, background: 'var(--bg-surface)',
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              MILESTONES_COMPLETED gate (25-34)
                            </div>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                              <input type="checkbox" checked={step5MilestonesAffirmed}
                                onChange={(e) => setStep5MilestonesAffirmed(e.target.checked)} />
                              <span>I affirm each milestone represents a reasonable measure of progress.</span>
                            </label>
                            <textarea
                              value={step5MilestonesNotes}
                              onChange={(e) => setStep5MilestonesNotes(e.target.value)}
                              placeholder="Reasonable-measure rationale per milestone..."
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)',
                                resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                              }}
                            />
                          </div>
                        )}

                        {/* Conditional: UNITS_DELIVERED */}
                        {step5HasUnits && (
                          <div style={{
                            padding: 10, border: '1px solid var(--border-subtle)',
                            borderRadius: 4, background: 'var(--bg-surface)',
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              UNITS_DELIVERED gate (25-36)
                            </div>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                              <input type="checkbox" checked={step5UnitsAffirmed}
                                onChange={(e) => setStep5UnitsAffirmed(e.target.checked)} />
                              <span>I affirm excluded_units_count entries are correctly identified.</span>
                            </label>
                            <textarea
                              value={step5UnitsNotes}
                              onChange={(e) => setStep5UnitsNotes(e.target.value)}
                              placeholder="Units carve-out rationale..."
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)',
                                resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                              }}
                            />
                          </div>
                        )}

                        {/* Conditional: R2I expedient */}
                        {step5HasR2i && (
                          <div style={{
                            padding: 10, border: '1px solid var(--border-subtle)',
                            borderRadius: 4, background: 'var(--bg-surface)',
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              Right-to-invoice practical expedient (55-18)
                            </div>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                              <input type="checkbox" checked={step5R2iAffirmed}
                                onChange={(e) => setStep5R2iAffirmed(e.target.checked)} />
                              <span>I affirm invoice amount corresponds directly to value transferred for each R2I POB.</span>
                            </label>
                            <textarea
                              value={step5R2iNotes}
                              onChange={(e) => setStep5R2iNotes(e.target.value)}
                              placeholder="R2I eligibility rationale..."
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)',
                                resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                              }}
                            />
                          </div>
                        )}

                        {/* Conditional: onerous contract */}
                        {step5HasOpenOnerous && (
                          <div style={{
                            padding: 10, border: '1px solid var(--warning)',
                            borderRadius: 4, background: 'rgba(234,179,8,0.05)',
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--warning)' }}>
                              Onerous contract review (ASC 450 / IAS 37)
                            </div>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                              <input type="checkbox" checked={step5OnerousAffirmed}
                                onChange={(e) => setStep5OnerousAffirmed(e.target.checked)} />
                              <span>I reviewed the onerous-contract assessment and documented the loss-provision disposition.</span>
                            </label>
                            <textarea
                              value={step5OnerousNotes}
                              onChange={(e) => setStep5OnerousNotes(e.target.value)}
                              placeholder="Onerous-contract disposition note..."
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)',
                                resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {mode === 'VIEW' && selected.step5_over_time_qualified_affirmed !== null && (
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                        Over-time Qualified: {selected.step5_over_time_qualified_affirmed ? 'Yes' : 'No'}
                        {' · '}Progress Method: {selected.step5_progress_method_affirmed ? 'Affirmed' : '—'}
                        {selected.step5_cost_baseline_affirmed !== null && (
                          <div>Cost baseline: {selected.step5_cost_baseline_affirmed ? 'Affirmed' : '—'}
                          {selected.step5_uninstalled_materials_carved_out_affirmed !== null && `, 25-36 carve-out: ${selected.step5_uninstalled_materials_carved_out_affirmed ? 'Affirmed' : '—'}`}
                          </div>
                        )}
                        {selected.step5_milestones_reasonable_measure_affirmed !== null && (
                          <div>Milestones (25-34): {selected.step5_milestones_reasonable_measure_affirmed ? 'Affirmed' : '—'}</div>
                        )}
                        {selected.step5_units_carve_out_affirmed !== null && (
                          <div>Units (25-36): {selected.step5_units_carve_out_affirmed ? 'Affirmed' : '—'}</div>
                        )}
                        {selected.step5_r2i_expedient_affirmed !== null && (
                          <div>R2I (55-18): {selected.step5_r2i_expedient_affirmed ? 'Affirmed' : '—'}</div>
                        )}
                        {selected.step5_onerous_contract_reviewed !== null && (
                          <div>Onerous (ASC 450): {selected.step5_onerous_contract_reviewed ? 'Affirmed' : '—'}</div>
                        )}
                        {selected.step5_approved_at && (
                          <div>Step 5 approved: {new Date(selected.step5_approved_at).toLocaleString()}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ASC 340-40 Phase 12d — Step 6 Contract Cost Attestations */}
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                      Step 6 — Contract Costs (ASC 340-40-25-1/25-3/25-4/25-5 + 35-5/35-6)
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
                      Capitalization, amortization pattern, impairment testing, and 25-4
                      expedient election attestations. Fires when any cost asset exists on
                      this contract.
                    </div>

                    {/* Pre-flight hard-block banners */}
                    {step6PreflightBlocks.length > 0 && (
                      <div style={{
                        padding: 10, marginBottom: 10,
                        background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)',
                        borderRadius: 4, fontSize: 12, color: 'var(--negative)',
                      }}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>
                          Step 6 hard block — approval will fail:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {step6PreflightBlocks.map((code) => (
                            <li key={code}>
                              <strong>{code}</strong>
                              {code === 'STEP6_PENDING_IMPAIRMENT_UNRESOLVED' && (
                                <> — drain via Progress Tracker &gt; Contract Costs &gt; Impairment Testing &gt; Consume Pending, OR cancel the PENDING row.</>
                              )}
                              {code === 'STEP6_FAILED_IMPAIRMENT_REQUIRES_REVIEW' && (
                                <> — investigate the FAILED row in Impairment Testing; cancel or re-test after correcting data.</>
                              )}
                              {code === 'STEP6_COST_ASSET_IMPAIRED_NOT_CONSUMED' && (
                                <> — a cost asset is stuck at status=IMPAIRED. Run Consume Pending on any remaining PENDING rows.</>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {!step6HasAnyCostAsset && step6PreflightBlocks.length === 0 && (
                      <div style={{
                        padding: 10, marginBottom: 10,
                        background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                        borderRadius: 4, fontSize: 12, color: 'var(--text-secondary)',
                      }}>
                        No ASC 340-40 cost assets on this contract — Step 6 is trivially
                        satisfied. Attestations default to True on approval.
                      </div>
                    )}

                    {step6HasAnyCostAsset && step6PreflightBlocks.length === 0 && mode !== 'VIEW' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {/* Always-required: contract-costs reviewed */}
                        <div style={{
                          padding: 10, border: '1px solid var(--border-subtle)',
                          borderRadius: 4, background: 'var(--bg-surface)',
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            Contract costs reviewed (340-40-25-1/25-5)
                          </div>
                          <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                            <input type="checkbox" checked={step6CcReviewed}
                              onChange={(e) => setStep6CcReviewed(e.target.checked)} />
                            <span>I affirm all capitalized / expensed cost assets on this contract have been reviewed per 340-40-25-1 / 25-5 capitalization criteria.</span>
                          </label>
                          <textarea
                            value={step6CcReviewedNotes}
                            onChange={(e) => setStep6CcReviewedNotes(e.target.value)}
                            placeholder="Cost assets reviewed — summary of capitalization decisions..."
                            style={{
                              width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)',
                              resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                            }}
                          />
                        </div>

                        {/* Always-required: recoverability (340-40-25-3) */}
                        <div style={{
                          padding: 10, border: '1px solid var(--border-subtle)',
                          borderRadius: 4, background: 'var(--bg-surface)',
                        }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>
                            Recoverability (340-40-25-3)
                          </div>
                          <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                            <input type="checkbox" checked={step6Recoverable}
                              onChange={(e) => setStep6Recoverable(e.target.checked)} />
                            <span>I affirm the entity reasonably expects to recover the capitalized contract costs.</span>
                          </label>
                          <textarea
                            value={step6RecoverableNotes}
                            onChange={(e) => setStep6RecoverableNotes(e.target.value)}
                            placeholder="Recovery basis — customer revenue forecast, creditworthiness, etc."
                            style={{
                              width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)',
                              resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                            }}
                          />
                        </div>

                        {/* Conditional: capitalized assets — amortization method */}
                        {step6HasCapitalizedAsset && (
                          <div style={{
                            padding: 10, border: '1px solid var(--border-subtle)',
                            borderRadius: 4, background: 'var(--bg-surface)',
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              Amortization method (340-40-35-5)
                            </div>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                              <input type="checkbox" checked={step6AmortMethod}
                                onChange={(e) => setStep6AmortMethod(e.target.checked)} />
                              <span>I affirm each capitalized asset's amortization pattern matches the transfer of G/S to which it relates.</span>
                            </label>
                            <textarea
                              value={step6AmortMethodNotes}
                              onChange={(e) => setStep6AmortMethodNotes(e.target.value)}
                              placeholder="Method justification per asset (POB_PROGRESS / STRAIGHT_LINE / POINT_IN_TIME)..."
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)',
                                resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                              }}
                            />
                          </div>
                        )}

                        {/* Conditional: impairment history */}
                        {step6HasImpairmentHistory && (
                          <div style={{
                            padding: 10, border: '1px solid var(--border-subtle)',
                            borderRadius: 4, background: 'var(--bg-surface)',
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              Impairment testing (340-40-35-6)
                            </div>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                              <input type="checkbox" checked={step6ImpTested}
                                onChange={(e) => setStep6ImpTested(e.target.checked)} />
                              <span>I affirm the impairment tests were appropriately designed, executed, and dispositioned.</span>
                            </label>
                            <textarea
                              value={step6ImpTestedNotes}
                              onChange={(e) => setStep6ImpTestedNotes(e.target.value)}
                              placeholder="Test design notes — period, recoverable amount basis, reviewer disposition..."
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)',
                                resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                              }}
                            />
                          </div>
                        )}

                        {/* Conditional: 25-4 expedient election */}
                        {step6HasExpedientAsset && (
                          <div style={{
                            padding: 10, border: '1px solid var(--border-subtle)',
                            borderRadius: 4, background: 'var(--bg-surface)',
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                              25-4 Practical expedient election
                            </div>
                            <label style={{ display: 'flex', gap: 6, cursor: 'pointer', fontSize: 11, marginBottom: 4 }}>
                              <input type="checkbox" checked={step6Expedient}
                                onChange={(e) => setStep6Expedient(e.target.checked)} />
                              <span>I affirm the 340-40-25-4 practical expedient election (expense immediately for amortization period ≤ tenant threshold) was appropriate for each asset.</span>
                            </label>
                            <textarea
                              value={step6ExpedientNotes}
                              onChange={(e) => setStep6ExpedientNotes(e.target.value)}
                              placeholder="Expedient rationale per asset — expected period, decision basis..."
                              style={{
                                width: '100%', marginTop: 4, padding: 4, fontSize: 11,
                                background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                                borderRadius: 4, color: 'var(--text-primary)',
                                resize: 'vertical', minHeight: 40, fontFamily: 'inherit',
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {mode === 'VIEW' && selected.step6_contract_costs_reviewed_affirmed !== null && (
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                        Contract costs reviewed: {selected.step6_contract_costs_reviewed_affirmed ? 'Yes' : 'No'}
                        {' · '}Recoverable: {selected.step6_recoverable_affirmed ? 'Affirmed' : '—'}
                        {selected.step6_amortization_method_affirmed !== null && (
                          <div>Amortization method (35-5): {selected.step6_amortization_method_affirmed ? 'Affirmed' : '—'}</div>
                        )}
                        {selected.step6_impairment_tested_affirmed !== null && (
                          <div>Impairment testing (35-6): {selected.step6_impairment_tested_affirmed ? 'Affirmed' : '—'}</div>
                        )}
                        {selected.step6_25_4_expedient_election_affirmed !== null && (
                          <div>25-4 expedient: {selected.step6_25_4_expedient_election_affirmed ? 'Affirmed' : '—'}</div>
                        )}
                        {selected.step6_approved_at && (
                          <div>Step 6 approved: {new Date(selected.step6_approved_at).toLocaleString()}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Phase 9d — Escalation / CPI events + cumulative catch-up */}
                  <div style={{
                    marginTop: 10, padding: 8, fontSize: 11,
                    background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                      <span>Escalation Events (ASC 606-10-32-14 cumulative catch-up)</span>
                      {escalationHistory.length > 0 && (
                        <span style={{
                          fontSize: 10, padding: '1px 6px', borderRadius: 8,
                          background: 'rgba(59,130,246,0.15)', color: 'var(--accent)',
                          fontWeight: 600,
                        }}>{escalationHistory.length} event{escalationHistory.length === 1 ? '' : 's'}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                      CPI / index / annual escalator events. Each application rescales
                      future schedule lines and posts a single catch-up JE for already-
                      recognised revenue (past schedule lines are immutable per 32-14).
                    </div>
                    {escalationHistory.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        {escalationHistory.map(ev => (
                          <div key={ev.id} style={{
                            padding: 4, marginBottom: 3,
                            background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                            borderRadius: 3,
                          }}>
                            <div style={{ display: 'flex', gap: 8, color: 'var(--text-secondary)' }}>
                              <span>{ev.changed_at?.slice(0, 10)}</span>
                              <span>Δ <strong style={{ color: (ev.delta_amount || 0) >= 0 ? 'var(--positive)' : 'var(--negative)' }}>
                                ${(ev.delta_amount || 0).toLocaleString()}
                              </strong></span>
                            </div>
                            {ev.rationale && (
                              <div style={{ color: 'var(--text-hint)', fontStyle: 'italic' }}>{ev.rationale}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {mode !== 'VIEW' && (
                      <div style={{ padding: 6, background: 'var(--bg-surface)', borderRadius: 4 }}>
                        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                          <input
                            type="number"
                            step="0.01"
                            value={escalationDelta}
                            onChange={(e) => setEscalationDelta(e.target.value)}
                            placeholder="Delta ($) — positive=up, negative=down"
                            style={{
                              flex: 2, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)',
                            }}
                          />
                          <input
                            type="date"
                            value={escalationDate}
                            onChange={(e) => setEscalationDate(e.target.value)}
                            aria-label="Escalation effective date"
                            title="Escalation effective date"
                            style={{
                              flex: 1, padding: 4, fontSize: 11,
                              background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                              borderRadius: 4, color: 'var(--text-primary)',
                            }}
                          />
                        </div>
                        <input
                          type="text"
                          value={escalationDesc}
                          onChange={(e) => setEscalationDesc(e.target.value)}
                          placeholder="Description (CPI index + period, clause ref, etc.)"
                          style={{
                            width: '100%', marginBottom: 4, padding: 4, fontSize: 11,
                            background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                            borderRadius: 4, color: 'var(--text-primary)',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={submitEscalation}
                            disabled={escalationSubmitting}
                            style={{
                              padding: '3px 10px', fontSize: 10, fontWeight: 600,
                              background: 'var(--accent)', color: 'white',
                              border: 'none', borderRadius: 3,
                              cursor: escalationSubmitting ? 'wait' : 'pointer',
                              opacity: escalationSubmitting ? 0.7 : 1,
                            }}
                          >
                            {escalationSubmitting ? 'Applying…' : 'Apply Escalation'}
                          </button>
                          {escalationError && (
                            <span style={{ color: 'var(--negative)', fontSize: 11 }}>{escalationError}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {refundLiabilities.length > 0 && (
                    <div style={{
                      marginTop: 10, padding: 8, fontSize: 11,
                      background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)',
                      borderRadius: 4,
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        Refund Liabilities (ASC 606-10-32-10)
                      </div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {refundLiabilities.map(rl => (
                          <div key={rl.id} style={{
                            padding: '4px 8px',
                            background: rl.status === 'OPEN' ? 'rgba(234,179,8,0.15)'
                              : rl.status === 'ACCRUED' ? 'rgba(59,130,246,0.15)'
                              : rl.status === 'SETTLED' ? 'rgba(34,197,94,0.15)'
                              : 'rgba(148,163,184,0.15)',
                            color: rl.status === 'OPEN' ? 'var(--warning)'
                              : rl.status === 'ACCRUED' ? 'var(--accent)'
                              : rl.status === 'SETTLED' ? 'var(--positive)'
                              : 'var(--text-secondary)',
                            borderRadius: 3,
                          }}>
                            <span style={{ fontWeight: 600 }}>{rl.status}</span>
                            {' · initial=$'}{rl.initial_amount.toLocaleString()}
                            {rl.status === 'ACCRUED' && ` · balance=$${rl.liability_balance.toLocaleString()}`}
                            {rl.status === 'SETTLED' && ` · settled=$${rl.settled_amount.toLocaleString()}`}
                            {rl.status === 'RELEASED' && rl.released_amount > 0 && ` · released=$${rl.released_amount.toLocaleString()}`}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ASC 606 Phase 8g — Lease/Non-Lease Bifurcation section */}
            <div style={{
              padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 6,
              background: 'var(--bg-surface)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Lease / Non-Lease Bifurcation (ASC 842-10-15-28+)</span>
                {mode !== 'VIEW' && selected.review_status === 'PENDING'
                  && components && !components.components.some(c => c.component_type === 'LEASE') && (
                  <button
                    type="button"
                    onClick={() => {
                      setBifurcateForm({
                        allocated_amount: '', description: '', asset_type: '',
                        lessor_name: selected.customer_name || '',
                        commencement_date: selected.start_date || '',
                        end_date: selected.end_date || '',
                        monthly_payment: '', discount_rate: '0.05',
                        framework: 'IFRS16', classification: 'OPERATING',
                        basis_note: '',
                      });
                      setBifurcateError(null);
                      setBifurcateModalOpen(true);
                    }}
                    style={{
                      padding: '3px 10px', fontSize: 10, fontWeight: 600,
                      background: 'var(--accent)', color: 'white',
                      border: 'none', borderRadius: 3, cursor: 'pointer',
                    }}
                  >Bifurcate Lease…</button>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                If this contract contains a lease element (identified asset the customer
                controls), allocate the portion to M18 (ROU asset + lease liability) and
                leave the residual on ASC 606 recognition. Executed as a single transaction.
              </div>
              {componentsLoading && (
                <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>Loading components…</div>
              )}
              {!componentsLoading && components && components.components.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  No bifurcation applied. Current contract value: {formatAmount(components.current_total_value)}.
                </div>
              )}
              {!componentsLoading && components && components.components.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {components.components.map((c) => (
                    <div key={c.id} style={{
                      padding: 8,
                      border: `1px solid ${c.component_type === 'LEASE' ? 'var(--accent)' : 'var(--border-subtle)'}`,
                      borderRadius: 4,
                      background: c.component_type === 'LEASE' ? 'rgba(59,130,246,0.05)' : 'var(--bg-card)',
                      fontSize: 12,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <span style={{
                            padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                            background: c.component_type === 'LEASE' ? 'var(--accent)' : 'var(--text-hint)',
                            color: 'white',
                          }}>{c.component_type}</span>
                          {c.lease_id && (
                            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--accent)' }}>
                              → M18 Lease #{c.lease_id}
                            </span>
                          )}
                        </div>
                        <span style={{ fontWeight: 600, flexShrink: 0 }}>{formatAmount(c.allocated_amount)}</span>
                      </div>
                      {c.basis_note && (
                        <div style={{ marginTop: 4, fontSize: 10, color: 'var(--text-secondary)' }}>
                          {c.basis_note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Collectibility Assessment (606-10-25-1(e))</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                {(['PROBABLE', 'NOT_PROBABLE'] as const).map((s) => (
                  <label
                    key={s}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                      border: `1px solid ${collectibilityStatus === s ? 'var(--accent)' : 'var(--border-subtle)'}`,
                      background: collectibilityStatus === s ? 'rgba(59,130,246,0.08)' : 'var(--bg-surface)',
                      borderRadius: 4, cursor: mode === 'VIEW' ? 'default' : 'pointer', fontSize: 12,
                    }}
                  >
                    <input
                      type="radio"
                      checked={collectibilityStatus === s}
                      disabled={mode === 'VIEW'}
                      onChange={() => setCollectibilityStatus(s)}
                      style={{ cursor: mode === 'VIEW' ? 'default' : 'pointer' }}
                    />
                    {s.replace('_', ' ')}
                  </label>
                ))}
              </div>
              <textarea
                value={collectibilityNotes}
                disabled={mode === 'VIEW'}
                onChange={(e) => setCollectibilityNotes(e.target.value)}
                placeholder="Collectibility evidence (credit check, payment history, counterparty financials)..."
                style={{ width: '100%', padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 50, fontFamily: 'inherit' }}
              />
            </div>

            {mode === 'REJECT' && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--negative)' }}>Rejection Reason (required)</div>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Why is this contract being rejected? Which criterion failed?"
                  style={{ width: '100%', padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: `1px solid ${rejectionReason.trim() ? 'var(--border-subtle)' : 'var(--negative)'}`, borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 60, fontFamily: 'inherit' }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Phase 7f — combine confirmation sub-modal */}
      <Modal
        open={combineModalOpen}
        onClose={() => !combineSubmitting && setCombineModalOpen(false)}
        title="Combine Contracts (ASC 606-10-25-9)"
        description={
          combineTarget && selected
            ? `${combineTarget.contract_number} → will be combined INTO ${selected.contract_number}`
            : undefined
        }
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={() => !combineSubmitting && setCombineModalOpen(false)}
              disabled={combineSubmitting}
              style={{ padding: '8px 16px', fontSize: 13, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 4, cursor: combineSubmitting ? 'wait' : 'pointer' }}
            >Cancel</button>
            <button
              type="button"
              onClick={submitCombine}
              disabled={combineSubmitting || !combineReason.trim()}
              style={{
                padding: '8px 16px', fontSize: 13, fontWeight: 600,
                background: combineReason.trim() ? 'var(--accent)' : 'var(--bg-hover)',
                color: combineReason.trim() ? 'white' : 'var(--text-hint)',
                border: 'none', borderRadius: 4,
                cursor: combineSubmitting ? 'wait' : (combineReason.trim() ? 'pointer' : 'not-allowed'),
              }}
            >{combineSubmitting ? 'Combining…' : 'Combine'}</button>
          </>
        }
      >
        {combineTarget && selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { label: 'PRIMARY (kept)', c: { contract_number: selected.contract_number, contract_date: null, total_value: selected.total_value, status: selected.contract_status } },
                { label: 'SECONDARY (linked)', c: combineTarget },
              ].map((side, i) => (
                <div key={i} style={{ padding: 10, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>{side.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{side.c.contract_number}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {side.c.contract_date ? `Signed ${side.c.contract_date} · ` : ''}${Number(side.c.total_value).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 2 }}>{side.c.status}</div>
                </div>
              ))}
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Reason (required) *</span>
              <textarea
                value={combineReason}
                onChange={(e) => setCombineReason(e.target.value)}
                placeholder="Cite the ASC 606-10-25-9 criterion: (a) package with single commercial objective; (b) consideration interdependent; or (c) single POB."
                style={{
                  width: '100%', padding: 6, fontSize: 12,
                  background: 'var(--bg-surface)',
                  border: `1px solid ${combineReason.trim() ? 'var(--border-subtle)' : 'var(--negative)'}`,
                  borderRadius: 4, color: 'var(--text-primary)',
                  resize: 'vertical', minHeight: 70, fontFamily: 'inherit',
                }}
              />
            </label>
            <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>
              DOA-gated (category REVENUE_CONTRACT_COMBINATION) against the sum of both contracts' total values.
              Phase 7f records the link for audit; POB rollup follows in Phase 8 Step 2.
            </div>
          </div>
        )}
      </Modal>

      {/* Phase 8f — POB Merge Modal */}
      <Modal
        open={mergeModalOpen}
        onClose={() => !mergeSubmitting && setMergeModalOpen(false)}
        title={mergeGroup ? `Merge POBs — ${mergeGroup.combine_reason}` : 'Merge POBs'}
        description="Collapse not-distinct POBs into a single surviving head. First listed POB is the head; others are absorbed. Method re-derived from first principles per ASC 606-10-25-14 to 25-22."
        size="md"
        footer={
          <>
            <button
              onClick={() => !mergeSubmitting && setMergeModalOpen(false)}
              disabled={mergeSubmitting}
              style={{ padding: '8px 16px', fontSize: 13, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 4, cursor: 'pointer' }}
            >Cancel</button>
            <button
              onClick={submitMerge}
              disabled={mergeSubmitting || !mergeJustification.trim()}
              style={{
                padding: '8px 16px', fontSize: 13, fontWeight: 600,
                background: mergeJustification.trim() ? 'var(--warning)' : 'var(--bg-hover)',
                color: mergeJustification.trim() ? 'white' : 'var(--text-hint)',
                border: 'none', borderRadius: 4,
                cursor: mergeSubmitting ? 'wait' : (mergeJustification.trim() ? 'pointer' : 'not-allowed'),
              }}
            >{mergeSubmitting ? 'Merging…' : 'Execute Merge'}</button>
          </>
        }
      >
        {mergeGroup && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {mergeError && (
              <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)', borderRadius: 4, color: 'var(--negative)', fontSize: 12 }}>
                {mergeError}
              </div>
            )}
            <div style={{ padding: 10, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {mergeGroup.members.length} POBs will be merged:
              </div>
              {mergeGroup.members.map((m, i) => (
                <div key={m.pob_id} style={{ marginBottom: 2, color: 'var(--text-secondary)' }}>
                  {i === 0 ? (
                    <span style={{ color: 'var(--positive)', fontWeight: 600 }}>HEAD →</span>
                  ) : (
                    <span style={{ color: 'var(--text-hint)' }}>absorbed →</span>
                  )}
                  {' '}POB #{m.po_number} — {m.obligation_name || '(unnamed)'}
                </div>
              ))}
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Justification (required) *</span>
              <textarea
                value={mergeJustification}
                onChange={(e) => setMergeJustification(e.target.value)}
                placeholder="Rationale per ASC 606-10-25-21 — goes into audit trail."
                style={{
                  width: '100%', padding: 6, fontSize: 12,
                  background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                  borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 60,
                  fontFamily: 'inherit',
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>
                Forced recognition method (required only if POBs span mixed methods)
              </span>
              <select
                value={mergeForcedMethod}
                onChange={(e) => setMergeForcedMethod(e.target.value)}
                style={{
                  padding: 6, fontSize: 12,
                  background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                  borderRadius: 4, color: 'var(--text-primary)',
                }}
              >
                <option value="">Inherit if uniform (recommended)</option>
                <option value="POINT_IN_TIME">POINT_IN_TIME</option>
                <option value="RATABLE">RATABLE</option>
                <option value="MILESTONE">MILESTONE</option>
                <option value="USAGE">USAGE</option>
              </select>
            </label>
            <div style={{ fontSize: 10, color: 'var(--text-hint)', lineHeight: 1.4 }}>
              Preconditions: all non-head POBs must have combine_reason set (the Phase 8a
              classifier does this), none may have recognized revenue, none may already be
              BUNDLED. Head's allocated_price becomes SUM of children. SCHEDULED (not yet
              recognized) lines on children are cleared. Split is available to reverse.
            </div>
          </div>
        )}
      </Modal>

      {/* Phase 8g — Lease Bifurcation Modal */}
      <Modal
        open={bifurcateModalOpen}
        onClose={() => !bifurcateSubmitting && setBifurcateModalOpen(false)}
        title="Bifurcate Lease Component (ASC 842-10-15-28+)"
        description="Allocate a portion of the contract to a lease component. Creates an M18 Lease (DRAFT) and reduces the RevenueContract total to the residual. Single-transaction — rollback on failure."
        size="lg"
        footer={
          <>
            <button
              onClick={() => !bifurcateSubmitting && setBifurcateModalOpen(false)}
              disabled={bifurcateSubmitting}
              style={{ padding: '8px 16px', fontSize: 13, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 4, cursor: 'pointer' }}
            >Cancel</button>
            <button
              onClick={submitBifurcate}
              disabled={bifurcateSubmitting}
              style={{
                padding: '8px 16px', fontSize: 13, fontWeight: 600,
                background: 'var(--accent)', color: 'white',
                border: 'none', borderRadius: 4,
                cursor: bifurcateSubmitting ? 'wait' : 'pointer',
              }}
            >{bifurcateSubmitting ? 'Bifurcating…' : 'Execute Bifurcation'}</button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {bifurcateError && (
            <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)', borderRadius: 4, color: 'var(--negative)', fontSize: 12 }}>
              {bifurcateError}
            </div>
          )}
          {selected && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              Contract {selected.contract_number} · total {formatAmount(selected.total_value)}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Allocated to lease *</span>
              <input type="number" min="0" step="0.01"
                value={bifurcateForm.allocated_amount}
                onChange={(e) => setBifurcateForm({ ...bifurcateForm, allocated_amount: e.target.value })}
                style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Monthly payment *</span>
              <input type="number" min="0" step="0.01"
                value={bifurcateForm.monthly_payment}
                onChange={(e) => setBifurcateForm({ ...bifurcateForm, monthly_payment: e.target.value })}
                style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Start date *</span>
              <input type="date"
                value={bifurcateForm.commencement_date}
                onChange={(e) => setBifurcateForm({ ...bifurcateForm, commencement_date: e.target.value })}
                style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>End date *</span>
              <input type="date"
                value={bifurcateForm.end_date}
                onChange={(e) => setBifurcateForm({ ...bifurcateForm, end_date: e.target.value })}
                style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Discount rate (for PV)</span>
              <input type="number" min="0" max="1" step="0.001"
                value={bifurcateForm.discount_rate}
                onChange={(e) => setBifurcateForm({ ...bifurcateForm, discount_rate: e.target.value })}
                style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Framework</span>
              <select
                value={bifurcateForm.framework}
                onChange={(e) => setBifurcateForm({ ...bifurcateForm, framework: e.target.value })}
                style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              >
                <option value="IFRS16">IFRS 16</option>
                <option value="ASC842">ASC 842</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Asset type</span>
              <input
                value={bifurcateForm.asset_type}
                onChange={(e) => setBifurcateForm({ ...bifurcateForm, asset_type: e.target.value })}
                placeholder="EQUIPMENT / VEHICLE / REAL_ESTATE"
                style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>Classification</span>
              <select
                value={bifurcateForm.classification}
                onChange={(e) => setBifurcateForm({ ...bifurcateForm, classification: e.target.value })}
                style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
              >
                <option value="OPERATING">Operating</option>
                <option value="FINANCE">Finance</option>
              </select>
            </label>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600 }}>Asset description *</span>
            <input
              value={bifurcateForm.description}
              onChange={(e) => setBifurcateForm({ ...bifurcateForm, description: e.target.value })}
              placeholder="e.g. Industrial press — customer directs use for 36 months"
              style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600 }}>Lessor name (defaults to customer)</span>
            <input
              value={bifurcateForm.lessor_name}
              onChange={(e) => setBifurcateForm({ ...bifurcateForm, lessor_name: e.target.value })}
              style={{ padding: 6, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 4, color: 'var(--text-primary)' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600 }}>Basis note (rationale for allocation)</span>
            <textarea
              value={bifurcateForm.basis_note}
              onChange={(e) => setBifurcateForm({ ...bifurcateForm, basis_note: e.target.value })}
              placeholder="Why this allocation amount — cite ASC 842-10-15-33 basis (relative standalone price or observable input)."
              style={{
                width: '100%', padding: 6, fontSize: 11,
                background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                borderRadius: 4, color: 'var(--text-primary)', resize: 'vertical', minHeight: 40,
                fontFamily: 'inherit',
              }}
            />
          </label>
        </div>
      </Modal>

      {/* Phase 16a1 — Principal/Agent reviewer panel (opened from PA POB rows) */}
      {paPanelPob && (
        <PrincipalAgentAffirmationPanel
          pobId={paPanelPob.id}
          pobLabel={paPanelPob.label}
          onClose={() => setPaPanelPob(null)}
          onSuccess={() => {
            // Refresh the review list + selected review data so the new PA
            // classification shows in the parent modal.
            load();
            if (selected) openReview(selected, mode);
          }}
        />
      )}

      {/* Phase 16b1 — Warranty reviewer panel (opened from warranty POB rows) */}
      {warrantyPanelPob && (
        <WarrantyAssessmentPanel
          pobId={warrantyPanelPob.id}
          pobLabel={warrantyPanelPob.label}
          recognitionMethod={warrantyPanelPob.recognitionMethod || null}
          onClose={() => setWarrantyPanelPob(null)}
          onSuccess={() => {
            load();
            if (selected) openReview(selected, mode);
          }}
        />
      )}
    </PageLayout>
  );
}
